import { Injectable, inject } from '@angular/core';
import {  ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { camelCase, isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import Roles from 'src/assets/configData/roles.json';
import xmlFormatter from 'xml-formatter';
import { ApiConstants } from '../base/api-constants';
import { GlobalConstants } from '../base/global.constants';
import { HttpBaseService } from '../base/http-base.service';
import { nodeType } from '../pages/designs/data-model/data-model.component';
import { AppGlobalService } from './app.global.service';
import { ApplicationTypes } from './util.enum';
import { RoleConstants } from '../base/role.constants';
import { DatePipe } from '@angular/common';
import { AllowedFieldsConstants } from '../base/allowed-fields.constants';
import { Observable, Subject } from 'rxjs';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { getPendingService } from './pendingHttpServices.service';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { getAllKeywords } from '../STORE/selectors/util.selector';
import { userData } from '../STORE/selectors/auth.selector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

declare var $: any;
import { environment } from 'src/environments/environment';

// Standard environment order for consistent display across the application
export const ENV_ORDER = ['Dev', 'Test', 'Pre-Prod', 'Prod'];

/**
 * Sort environments so system-generated (isSysGen) environments appear first
 * in standard order (Dev, Test, Pre-Prod, Prod), followed by custom environments.
 */
export function sortEnvironments<T extends { name?: string; isSysGen?: boolean }>(environments: T[]): T[] {
  const sysGenEnvs = environments.filter((e) => e.isSysGen === true);
  const customEnvs = environments.filter((e) => e.isSysGen !== true);

  sysGenEnvs.sort((a, b) => {
    const indexA = ENV_ORDER.indexOf(a.name || '');
    const indexB = ENV_ORDER.indexOf(b.name || '');
    return (indexA === -1 ? ENV_ORDER.length : indexA) - (indexB === -1 ? ENV_ORDER.length : indexB);
  });

  return [...sysGenEnvs, ...customEnvs];
}

/**
 * Custom URL validator for URLs
 * Validates that the URL:
 * - Has no whitespace
 * - Does not end with '/'
 * - Starts with http:// or https://
 * - Does not contain query parameters
 * @param control - The form control to validate
 * @returns ValidationErrors or null
 */
export function validateUrl(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value || value.trim() === '') {
    return null; // Empty is allowed
  }

  if (/\s/.test(value)) {
    return { urlContainsWhitespace: true };
  }

  if (value.endsWith('/')) {
    return { urlTrailingSlash: true };
  }

  // Check for valid protocol (only http or https)
  const validProtocolPattern = /^https?:\/\/[^\s]+$/; //NOSONAR
  if (!validProtocolPattern.test(value)) {
    return { urlInvalidProtocol: true };
  }

  // Check for query params (not allowed in base URLs)
  if (value.includes('?')) {
    return { urlQueryParamsNotAllowed: true };
  }

  return null;
}

/**
 * Valid URL pattern for provider base URLs - requires http/https protocol with valid hostname structure
 * Used by validateProviderEnvUrl (base URLs without query params)
 */
export const VALID_PROVIDER_BASE_URL_PATTERN = /^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+(\:\d+)?(\/[^\s]*)?$/; //NOSONAR

/**
 * Valid URL pattern - allows query parameters
 * Used by validateExternalUrl (external URLs in menu items, home tiles, etc.)
 */
export const VALID_URL_PATTERN = /^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+(\:\d+)?(\/[^\s]*)?(\?[^\s]+)?$/; //NOSONAR

/**
 * Stricter URL validator for provider environment URLs
 * Validates that the URL:
 * - Has no whitespace
 * - Does not end with '/'
 * - Starts with http:// or https://
 * - Has a valid domain structure (hostname with TLD)
 * - Does not contain query parameters
 * @param control - The form control to validate
 * @returns ValidationErrors or null
 */
export function validateProviderEnvUrl(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value || value.trim() === '') {
    return null; // Empty is allowed
  }

  if (/\s/.test(value)) {
    return { urlContainsWhitespace: true };
  }

  if (value.endsWith('/')) {
    return { urlTrailingSlash: true };
  }

  // Check for valid URL format (http/https protocol with valid hostname structure)
  if (!VALID_PROVIDER_BASE_URL_PATTERN.test(value)) {
    return { urlInvalidFormat: true };
  }

  // Check for query params (not allowed in base URLs)
  if (value.includes('?')) {
    return { urlQueryParamsNotAllowed: true };
  }

  return null;
}

/**
 * Get specific error message based on URL validation error type
 * @param control - The form control with validation errors
 * @param translateService - Optional TranslateService for i18n
 * @returns The appropriate error message
 */
export function getUrlValidationErrorMessage(control: AbstractControl | null, translateService?: TranslateService): string {
  if (!control) {
    return '';
  }
  if (control.hasError('required')) {
    return translateService?.instant('DEV_URL_REQUIRED') || 'DEV URL is required';
  }
  if (control.hasError('urlTrailingSlash')) {
    return translateService?.instant('URL_TRAILING_SLASH') || "URL should not end with '/'";
  }
  if (control.hasError('urlContainsWhitespace')) {
    return translateService?.instant('URL_CONTAINS_WHITESPACE') || 'URL should not contain spaces';
  }
  if (control.hasError('urlInvalidProtocol')) {
    return translateService?.instant('URL_INVALID_PROTOCOL') || 'URL must start with http:// or https://';
  }
  if (control.hasError('urlInvalidFormat')) {
    return translateService?.instant('URL_INVALID_FORMAT') || 'URL must start with http:// or https:// and have a valid domain';
  }
  if (control.hasError('urlQueryParamsNotAllowed')) {
    return translateService?.instant('URL_QUERY_PARAMS_NOT_ALLOWED') || 'URL should not contain query parameters';
  }
  return translateService?.instant('URL_INVALID') || 'Please enter a valid URL';
}

/**
 * List of dangerous URL protocols that should be blocked for security
 */
export const DANGEROUS_URL_PROTOCOLS = ['javascript:', 'vbscript:', 'data:', 'file:'];

/**
 * @deprecated Use VALID_URL_PATTERN instead
 */
export const EXTERNAL_URL_PATTERN = VALID_URL_PATTERN;

/**
 * Interface for external URL validation result
 */
export interface ExternalUrlValidationResult {
  isValid: boolean;
  errorKey?: string;
  errorMessage?: string;
}

/**
 * Validates an external URL for menu items and home tiles
 * Checks for:
 * - Dangerous protocols (javascript:, vbscript:, data:, file:)
 * - Valid URL format (http/https protocol with valid hostname)
 * @param url - The URL string to validate
 * @returns ExternalUrlValidationResult indicating if validation passed
 */
export function validateExternalUrl(url: string | null | undefined): ExternalUrlValidationResult {
  if (!url || url.trim() === '') {
    return { isValid: true }; // Empty values handled elsewhere
  }

  const trimmedUrl = url.trim();
  const urlLower = trimmedUrl.toLowerCase();

  // Check for dangerous protocols
  for (const protocol of DANGEROUS_URL_PROTOCOLS) {
    if (urlLower.startsWith(protocol)) {
      return {
        isValid: false,
        errorKey: 'INVALID_URL_PROTOCOL',
        errorMessage: 'Please enter a valid URL'
      };
    }
  }

  // Check for valid URL format (allows query params for external URLs)
  if (!VALID_URL_PATTERN.test(trimmedUrl)) {
    return {
      isValid: false,
      errorKey: 'INVALID_URL_PROTOCOL',
      errorMessage: 'Please enter a valid URL'
    };
  }

  return { isValid: true };
}

/**
 * Interface for POJO name validation result
 */
export interface PojoValidationResult {
  isValid: boolean;
  errorKey?: string;
  errorMessage?: string;
}

/**
 * Validates that request POJO name and response POJO name are not the same
 * @param requestPojoName - The request POJO name
 * @param responsePojoName - The response POJO name
 * @returns PojoValidationResult indicating if validation passed
 */
export function validatePojoNamesNotSame(
  requestPojoName: string | undefined | null,
  responsePojoName: string | undefined | null
): PojoValidationResult {
  const trimmedRequest = requestPojoName?.trim();
  const trimmedResponse = responsePojoName?.trim();

  // Compare in lowercase for case-insensitive duplicate check
  if (trimmedRequest && trimmedResponse && trimmedRequest.toLowerCase() === trimmedResponse.toLowerCase()) {
    return {
      isValid: false,
      errorKey: 'REQUEST_RESPONSE_POJO_SAME_ERROR',
      errorMessage: 'Request POJO name and Response POJO name cannot be the same'
    };
  }
  return { isValid: true };
}

/**
 * Validates that POJO names are unique within the same functional component
 * @param requestPojoName - The request POJO name to validate
 * @param responsePojoName - The response POJO name to validate
 * @param currentApiSid - The SID of the current API being validated
 * @param currentFunctionalComponentId - The functional component ID of the current API
 * @param allApisData - Array of all API data in the provider
 * @returns PojoValidationResult indicating if validation passed
 */
export function validatePojoNamesUnique(
  requestPojoName: string | undefined | null,
  responsePojoName: string | undefined | null,
  currentApiSid: string,
  currentFunctionalComponentId: string,
  allApisData: any[]
): PojoValidationResult {
  if (!currentFunctionalComponentId || !allApisData?.length) {
    return { isValid: true };
  }

  const trimmedRequest = requestPojoName?.trim();
  const trimmedResponse = responsePojoName?.trim();

  // Get all APIs in the same functional component except the current one
  const apisInSameFunctionalComponent = allApisData.filter(
    (api: any) => api.functionalComponentId === currentFunctionalComponentId && api.sid !== currentApiSid
  );

  // Collect all POJO names from other APIs in the same functional component (converted to lowercase for case-insensitive comparison)
  const existingPojoNamesLower: string[] = [];
  apisInSameFunctionalComponent.forEach((api: any) => {
    if (api.requestPojoName?.trim()) {
      existingPojoNamesLower.push(api.requestPojoName.trim().toLowerCase());
    }
    if (api.responsePojoName?.trim()) {
      existingPojoNamesLower.push(api.responsePojoName.trim().toLowerCase());
    }
  });

  // Check if request POJO name is already used (case-insensitive)
  if (trimmedRequest && existingPojoNamesLower.includes(trimmedRequest.toLowerCase())) {
    return {
      isValid: false,
      errorKey: 'REQUEST_POJO_DUPLICATE_ERROR',
      errorMessage: 'Request POJO name is already used by another API in the same functional component'
    };
  }

  // Check if response POJO name is already used (case-insensitive)
  if (trimmedResponse && existingPojoNamesLower.includes(trimmedResponse.toLowerCase())) {
    return {
      isValid: false,
      errorKey: 'RESPONSE_POJO_DUPLICATE_ERROR',
      errorMessage: 'Response POJO name is already used by another API in the same functional component'
    };
  }

  return { isValid: true };
}

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  private appGlobalService = inject(AppGlobalService);
  private messageService = inject(MessageService);
  private bs = inject(HttpBaseService);
  private translate = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private store = inject(Store);
  private readonly router = inject(Router);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private keywordsSignal = this.store.selectSignal(getAllKeywords);
  private readonly pendingService = inject(getPendingService);
  private currentUser: any = null;
  private searchOverlayTrigger = new Subject<void>();
  searchOverlayTrigger$ = this.searchOverlayTrigger.asObservable();

  triggerSearchOverlay() {
    this.searchOverlayTrigger.next();
  }

  keywords: string[] = [];
  sqlKeywords: string[] = [];
  suiteTypes = ['Application', 'Database'];
  refAppMetaInfos = this.appGlobalService.get('refAppMetaInfos');

  constructor() {
    this.loadUserFromStore();
  }

  private loadUserFromStore() {
    this.store
      .select(userData)
      .pipe(takeUntilDestroyed())
      .subscribe((user: any) => {
        this.currentUser = user;
      });
  }

  getRestrictedKeywords(): string[] {
    return this.keywordsSignal()?.restrictedKeywords || [];
  }

  getRestrictedSQLKeywords(): string[] {
    return this.keywordsSignal()?.restrictedSqlKeywords || [];
  }

  getRestrictedTableKeywords(): string[] {
    return this.keywordsSignal()?.restrictedTableKeywords || [];
  }

  /**
   * Checks whether the given application (or the current app context) is a SUITE app.
   * @param applicationType - Optional. If provided, checks this value directly.
   *                          If omitted, reads from appGlobalService.
   * @returns true if the application type is SUITE.
   */
  isSuiteMode(applicationType?: string): boolean {
    const type = applicationType || this.appGlobalService.get('applicationType');
    return type === ApplicationTypes.SUITE;
  }

  /**
   * Checks whether the given application (or the current app context) is a MEMBER app.
   * @param applicationType - Optional. If provided, checks this value directly.
   *                          If omitted, reads from appGlobalService.
   * @returns true if the application type is MEMBER.
   */
  isMemberMode(applicationType?: string): boolean {
    const type = applicationType || this.appGlobalService.get('applicationType');
    return type === ApplicationTypes.MEMBER;
  }

  /**
   * @param applicationType - Optional. If provided, checks this value directly.
   * @returns true if the application type is SUITE or MEMBER.
   */
  isSuiteOrMember(applicationType?: string): boolean {
    return this.isSuiteMode(applicationType) || this.isMemberMode(applicationType);
  }

  /**
   * Check if the current user has permission to merge branches.
   * Only users with Architect role, sysAdmin, or devAdmin can merge branches.
   * Viewers and Prototype Viewers are explicitly restricted.
   */
  canMergeBranch(): boolean {
    try {
      const user = this.currentUser;
      if (user) {
        // sysAdmin and devAdmin have all privileges
        if (user.sysAdmin || user.devAdmin) {
          return true;
        }
        // Explicitly deny viewers
        const appRole = this.appGlobalService.get('appRole');
        const orgRole = user?.orgInfos?.[this.appGlobalService.get('orgId')];
        const restrictedRoles = new Set([RoleConstants.VIEWER, RoleConstants.PROTOTYPE_VIEWER, RoleConstants.DEVELOPMENT_VIEWER]);
        if (restrictedRoles.has(appRole) || restrictedRoles.has(orgRole)) {
          return false;
        }
        // Check app-level role (Architect)
        if (appRole === RoleConstants.ARCHITECT || orgRole === 'Architect') {
          return true;
        }
      }
    } catch (e) {
      console.error('Error checking merge branch permission:', e);
    }
    return false;
  }


  canCreateBranch(): boolean {
    return this.hasNonViewerRole();
  }

  canCreateTag(): boolean {
    return this.hasNonViewerRole();
  }

  /**
   * Check if the current user can delete a branch.
   * Without branchCreatedBy: general permission check (non-viewer).
   * With branchCreatedBy: only the branch owner or admin roles (sysAdmin/devAdmin) can delete.
   */
  /**
   * Check if the current user can delete a branch.
   * - If `isDefaultBranch` is true, deletion is not allowed.
   * - Without `branchCreatedBy`: general permission check (non-viewer roles).
   * - With `branchCreatedBy`: only the branch owner or admin roles (sysAdmin/devAdmin) can delete.
   */
  canDeleteBranch(branchCreatedBy?: string, isDefaultBranch?: boolean): boolean {
    // Prevent deletion of default branch regardless of role
    if (isDefaultBranch) return false;

    if (!this.hasNonViewerRole()) return false;
    if (branchCreatedBy) {
      const user = this.currentUser;
      if (!user) return false;
      if (user.sysAdmin || user.devAdmin) return true;
      const currentUserId = user.userId || user.sid;
      return !!(currentUserId && currentUserId === branchCreatedBy);
    }
    return true;
  }

  /**
   * Returns a tooltip key explaining why delete is disabled, or undefined if allowed.
   */
  getDeleteBranchTooltip(branchCreatedBy?: string, isDefaultBranch?: boolean): string | undefined {
    if (isDefaultBranch) return 'DEFAULT_BRANCH_CANNOT_BE_DELETED';
    if (!this.hasNonViewerRole()) return 'ONLY_BRANCH_OWNER_OR_ADMIN_CAN_DELETE';
    if (branchCreatedBy) {
      const user = this.currentUser;
      if (!user) return 'ONLY_BRANCH_OWNER_OR_ADMIN_CAN_DELETE';
      if (user.sysAdmin || user.devAdmin) return undefined;
      const currentUserId = user.userId || user.sid;
      if (!currentUserId || currentUserId !== branchCreatedBy) return 'ONLY_BRANCH_OWNER_OR_ADMIN_CAN_DELETE';
    }
    return undefined;
  }

  canViewChanges(): boolean {
    return true;
  }

  canViewMergeHistory(): boolean {
    return true;
  }

  canMarkAsDefault(): boolean {
    return this.hasNonViewerRole();
  }

  /**
   * Helper: Check if user has any role except App Viewer and Prototype Viewer.
   */
  private hasNonViewerRole(): boolean {
    try {
      const user = this.currentUser;
      if (user) {
        // sysAdmin and devAdmin have all privileges
        if (user.sysAdmin || user.devAdmin) {
          return true;
        }
        const appRole = this.appGlobalService.get('appRole');
        const orgRole = user?.orgInfos?.[this.appGlobalService.get('orgId')];
        // Deny if user is App Viewer or Prototype Viewer
        const restrictedRoles = new Set([RoleConstants.VIEWER, RoleConstants.PROTOTYPE_VIEWER]);
        if (restrictedRoles.has(appRole) || restrictedRoles.has(orgRole)) {
          return false;
        }
        // Allow all other roles
        return true;
      }
    } catch (e) {
      console.error('Error checking non-viewer role permission:', e);
    }
    return false;
  }

  dateFormats = [
    'd-MM-yy',
    'd-MM-y',
    'MM-dd-yy',
    'MM-dd-y',
    'dd-MM-yy',
    'y-MM-dd',
    'd/M/yy',
    'd/M/y',
    'd/MMM/yy',
    'd/MMM/y',
    'd MMM y',
    'd MMM yy',
    'M/d/yy',
    'M/d/y',
    'dd/MM/y',
    'dd-MM-y',
    'y/MM/dd',
    'dd MMM y',
    'dd/MMM/y',
    'dd-MMM-y',
    'MM-y',
    'y-MM',
    'y/MM',
    'dd/MM',
    'MMM dd, y',
    'MMM d, y',
    'MM/dd/yy',
    'MM/dd/y',
    'dd/MM/yy'
  ];
  dateTimeFormats = [
    'd-MM-yy h:mm a',
    'd-MM-yy h:mm:ss a',
    'd-MM-yy H:mm',
    'd-MM-yy H:mm:ss',
    'd-MM-y h:mm a',
    'd-MM-y h:mm:ss a',
    'd-MM-y H:mm',
    'd-MM-y H:mm:ss',
    'MM-dd-yy h:mm a',
    'MM-dd-yy h:mm:ss a',
    'MM-dd-yy H:mm',
    'MM-dd-yy H:mm:ss',
    'MM-dd-y h:mm a',
    'MM-dd-y h:mm:ss a',
    'MM-dd-y H:mm',
    'MM-dd-y H:mm:ss',
    'dd-MM-yy h:mm a',
    'dd-MM-yy h:mm:ss a',
    'dd-MM-yy H:mm',
    'dd-MM-yy H:mm:ss',
    'y-MM-dd h:mm a',
    'y-MM-dd h:mm:ss a',
    'y-MM-dd H:mm',
    'y-MM-dd H:mm:ss',
    'd/M/yy h:mm a',
    'd/M/yy h:mm:ss a',
    'd/M/yy H:mm',
    'd/M/yy H:mm:ss',
    'd/M/y h:mm a',
    'd/M/y h:mm:ss a',
    'd/M/y H:mm',
    'd/M/y H:mm:ss',
    'd/MMM/yy h:mm a',
    'd/MMM/yy h:mm:ss a',
    'd/MMM/yy H:mm',
    'd/MMM/yy H:mm:ss',
    'd/MMM/y h:mm a',
    'd/MMM/y h:mm:ss a',
    'd/MMM/y H:mm',
    'd/MMM/y H:mm:ss',
    'd MMM y h:mm a',
    'd MMM y h:mm:ss a',
    'd MMM y H:mm',
    'd MMM y H:mm:ss',
    'd MMM yy h:mm a',
    'd MMM yy h:mm:ss a',
    'd MMM yy H:mm',
    'd MMM yy H:mm:ss',
    'M/d/yy h:mm a',
    'M/d/yy h:mm:ss a',
    'M/d/yy H:mm',
    'M/d/yy H:mm:ss',
    'M/d/y h:mm a',
    'M/d/y h:mm:ss a',
    'M/d/y H:mm',
    'M/d/y H:mm:ss',
    'dd/MM/y h:mm a',
    'dd/MM/y h:mm:ss a',
    'dd/MM/y H:mm',
    'dd/MM/y H:mm:ss',
    'dd-MM-y h:mm a',
    'dd-MM-y h:mm:ss a',
    'dd-MM-y H:mm',
    'dd-MM-y H:mm:ss',
    'y/MM/dd h:mm a',
    'y/MM/dd h:mm:ss a',
    'y/MM/dd H:mm',
    'y/MM/dd H:mm:ss',
    'dd MMM y h:mm a',
    'dd MMM y h:mm:ss a',
    'dd MMM y H:mm',
    'dd MMM y H:mm:ss',
    'dd/MMM/y h:mm a',
    'dd/MMM/y h:mm:ss a',
    'dd/MMM/y H:mm',
    'dd/MMM/y H:mm:ss',
    'dd-MMM-y h:mm a',
    'dd-MMM-y h:mm:ss a',
    'dd-MMM-y H:mm',
    'dd-MMM-y H:mm:ss',
    'MM-y h:mm a',
    'MM-y h:mm:ss a',
    'MM-y H:mm',
    'MM-y H:mm:ss',
    'y-MM h:mm a',
    'y-MM h:mm:ss a',
    'y-MM H:mm',
    'y-MM H:mm:ss',
    'y/MM h:mm a',
    'y/MM h:mm:ss a',
    'y/MM H:mm',
    'y/MM H:mm:ss',
    'dd/MM h:mm a',
    'dd/MM h:mm:ss a',
    'dd/MM H:mm',
    'dd/MM H:mm:ss',
    'MMM dd, y h:mm a',
    'MMM dd, y h:mm:ss a',
    'MMM dd, y H:mm',
    'MMM dd, y H:mm:ss',
    'MMM d, y h:mm a',
    'MMM d, y h:mm:ss a',
    'MMM d, y H:mm',
    'MMM d, y H:mm:ss',
    'MM/dd/yy h:mm a',
    'MM/dd/yy h:mm:ss a',
    'MM/dd/yy H:mm',
    'MM/dd/yy H:mm:ss',
    'MM/dd/y h:mm a',
    'MM/dd/y h:mm:ss a',
    'MM/dd/y H:mm',
    'MM/dd/y H:mm:ss',
    'dd/MM/yy h:mm a',
    'dd/MM/yy h:mm:ss a',
    'dd/MM/yy H:mm',
    'dd/MM/yy H:mm:ss'
  ];
  failedStatus = [
    'PROJECT_CREATION_FAILED',
    'PREVALIDATION_FAILED_WITH_ERROR_MESSGAES',
    'PROTOTYPE_GENERATION_FAILED',
    'BE_GENERATION_FAILED',
    'PUSH_FAILED',
    'PULL_FAILED',
    'COMPILATION_FAILED',
    'AUTOMATION_BEGENERATION_FAILED',
    'PUBLISH_FAILED',
    'BACKEND_GENERATION_FAILED',
    'UI_LIBRARIES_PUSH_FAILED',
    'BE_LIBRARIES_PUSH_FAILED'
  ];
  successStatus = ['PROJECT_CERATION_COMPLETED', 'PUBLISH_COMPLETED', 'PUSH_COMPLETED', 'UI_LIBRARIES_PUSH_COMPLETED', 'BE_LIBRARIES_PUSH_COMPLETED'];

  // The app will be in non editable mode in these branch/tag creation statuses
  blockedStatus = [
    'BRANCH_CREATION_IN_PROGRESS',
    'TAG_CREATION_IN_PROGRESS',
    'SUITE_BRANCH_CREATION_IN_PROGRESS',
    'SUITE_TAG_CREATION_IN_PROGRESS',
    'BRANCH_VALIDATION_IN_PROGRESS',
    'TAG_VALIDATION_IN_PROGRESS'
  ];

  completedStatus = [
    'BRANCH_CREATION_COMPLETED',
    'TAG_CREATION_COMPLETED',
    'BRANCH_CREATION_FAILED',
    'TAG_CREATION_FAILED',
    'SUITE_BRANCH_CREATION_COMPLETED',
    'SUITE_TAG_CREATION_COMPLETED',
    'SUITE_BRANCH_CREATION_FAILED',
    'SUITE_TAG_CREATION_FAILED'
  ];
  titleMaxLength = 100;
  tableNameRegex = /^[a-zA-Z][a-zA-Z0-9 _]*$/;
  value: any;
  copySourceForJexcel: any;
  gitError = false;
  oktaValidation = false;
  cache = true;
  vpc = true;
  pasteStarted: any;

  checkForDefaultValues(property, formDefaultValues, actionModulerType?: any) {
    const val = property.defaultValue;
    if (val) {
      formDefaultValues[property.name] = val;
    } else if (property.uiType === 'multiselect') {
      formDefaultValues[property.name] = [];
    } else if (property.uiType === 'boolean' && val === false) {
      formDefaultValues[property.name] = val;
    } else {
      formDefaultValues[property.name] = null;
    }
    if (actionModulerType?.toLowerCase() === 'htmlcontainer' && formDefaultValues[property.name] === null) {
      formDefaultValues[property.name] = '';
    }
    if (property.uiType === 'checkbox') {
      const value = val === 'TRUE' || val === 'true';
      formDefaultValues[property.name] = value;
    }
    if (property.uiType === 'checkboxgroup') {
      if (typeof val === 'string') {
        const value = val.split(',');
        formDefaultValues[property.name] = value;
      } else {
        formDefaultValues[property.name] = val;
      }
    }
    if (property.uiType === 'iconselector' || property.uiType === 'lookup' || property.uiType === 'rolelookup') {
      if (!val) {
        formDefaultValues[property.name] = null;
      }
    }
    if (property.uiType === 'integer' || property.uiType === 'number') {
      if (val) {
        formDefaultValues[property.name] = Number(val);
      }
    }
  }
  setService(value) {
    this.value = value;
  }
  /**
   *  Convert HEX to HSL
   */
  hexToHsl(hex: string): string {
    // Remove the '#' if present
    hex = hex.replace(/^#/, '');

    // Parse the r, g, b values
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    // Find min and max values for RGB
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l: number = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
    }

    s = s * 100;
    l = l * 100;

    return `hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${l.toFixed(2)}%)`;
  }

  /**
   *  Convert HSL to HEX
   */
  hslToHex(hslStr): string {
    // Normalize the hue to be between 0 and 360
    const hslJson = this.getHslValues(hslStr);

    if (!hslJson) {
      return '#000000ff';
    }

    let h = hslJson?.h;
    let s = hslJson?.s;
    let l = hslJson?.l;
    const a = 1;
    h = h % 360;
    if (h < 0) {
      h += 360;
    }

    // Convert saturation and lightness from percentages to [0, 1]
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s; // Chroma
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1)); // Intermediate value
    const m = l - c / 2; // Match value for RGB conversion

    let r = 0,
      g = 0,
      b = 0;

    // Determine which sector of the color wheel the hue falls into
    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    // Convert the RGB values to the [0, 255] range and add the m component
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    // Convert the alpha value to the [0, 255] range
    const alpha = Math.round(a * 255);

    // Convert RGB and alpha values to hex and ensure 2 digits
    const hexR = r.toString(16).padStart(2, '0');
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');
    const hexA = alpha.toString(16).padStart(2, '0');

    // Return the combined hex value
    return `#${hexR}${hexG}${hexB}${hexA}`;
  }

  /**
   *  To get individual hsl values
   */
  getHslValues(hslString: string): { h: number; s: number; l: number } | null {
    const hslRegex = /^hsl\(\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\s*\)$/;
    const match = hslString.match(hslRegex);

    if (match) {
      const h = Number(match[1]); // Extract hue
      const s = Number(match[2]); // Extract saturation
      const l = Number(match[3]); // Extract lightness
      return { h, s, l };
    }

    return null; // Return null if the string is not in a valid HSL format
  }

  setCacheMandatory(value) {
    this.cache = value;
  }

  setVpcMandatory(value) {
    this.vpc = value;
  }
  setGitError(value) {
    this.gitError = value;
  }
  setOktaLoginValidation(value) {
    this.oktaValidation = value;
  }

  getSearchFields(flds: any, comType?) {
    const searchFields = { ...flds };
    Object.keys(searchFields).forEach((key) => {
      if (searchFields[key] === '' || !searchFields[key] || searchFields[key] === null) {
        delete searchFields[key];
      }
      switch (key) {
        case 'name':
        case 'shortName':
        case 'description':
          if (searchFields[key]) {
            searchFields[key] = `*${searchFields[key]}*`;
          }
          break;
        case 'tableId':
          searchFields[key] = searchFields[key]?.tableId;
          break;
        case 'workflowFieldId':
          searchFields[key] = searchFields[key]?.sid;
          break;
        default:
          if (searchFields[key]?.mName) {
            if (comType === 'LIFECYCLE') {
              searchFields[key] = searchFields[key]?.mName;
            } else {
              searchFields[key] = searchFields[key]?.sid;
            }
          }
          // Label/name can be passed from autocomplete in advance search like status, htp method, etc
          if (searchFields[key]?.label) {
            searchFields[key] = searchFields[key]?.label;
          }
          if (searchFields[key]?.name) {
            searchFields[key] = searchFields[key]?.name;
          }
          break;
      }
    });
    return searchFields;
  }

  replaceForDisplay(name1: string, name2: string, searchStrRaw): string {
    if (!searchStrRaw) {
      return '';
    }
    searchStrRaw = searchStrRaw.replace(new RegExp(name1, 'g'), name2);
    return searchStrRaw;
  }

  replaceAllForDisplay(searchFields) {
    let searchStr = this.getSearchString(searchFields);
    //replace "name" with "Diagram Name" and "description" with "Document Name" , "createdBy" with "Uploaded By" and "status" with "Status"
    searchStr = this.replaceForDisplay('name', 'Diagram Name', searchStr);
    searchStr = this.replaceForDisplay('description', 'Document Name', searchStr);
    searchStr = this.replaceForDisplay('createdBy', 'Created By', searchStr);
    searchStr = this.replaceForDisplay('modifiedBy', 'Modified By', searchStr);
    searchStr = this.replaceForDisplay('status', 'Status', searchStr);
    searchStr = this.replaceForDisplay('tableId', 'Table', searchStr);
    searchStr = this.replaceForDisplay('workflowFieldId', 'Workflow Field', searchStr);
    searchStr = this.replaceForDisplay('workflowName', 'Workflow Name', searchStr);
    return searchStr;
  }

  destroyFancyTreeSubscriptions($tree) {
    if ($tree.length === 0) return;
    try {
      const tree = $.ui.fancytree.getTree($tree);
      if (tree) {
        // FancyTree handles its own deep cleanup
        (tree as any).destroy(true);
      }

      // Only clean root handlers and data
      $tree.off();
      $tree.removeData();
      $.removeData($tree[0]);

      $tree.empty();
      $tree.remove();
    } catch (e) {
      console.warn('FancyTree cleanup failed:', e);
    }
  }

  getSearchString(searchFields: any) {
    let searchStr = '';
    Object.keys(searchFields).forEach((key) => {
      if (key !== 'appId' && key !== 'sid' && !(searchFields[key] === '' || !searchFields[key])) {
        if (this.appGlobalService.get('appmetaInfo')[searchFields[key]]) {
          searchFields[key] = this.appGlobalService.get('appmetaInfo')[searchFields[key]];
        }
        searchStr += `${key}:${searchFields[key]}; `;
      }

      if (key === 'sid' && !(searchFields[key] === '' || !searchFields[key])) {
        if (this.appGlobalService.get('appmetaInfo')[searchFields[key]]) {       
            searchStr += `Name:${this.appGlobalService.get('appmetaInfo')[searchFields[key]]}; `;
        }
      }

    });
    return searchStr;
  }

  getPropertyValues(properties, actionModulerType, nodeTypeField) {
    if (nodeTypeField && nodeTypeField === nodeType.FIELD) {
      const prps = properties.field.properties;
      return prps
        ?.filter((prp) => {
          return (
            (prp?.type !== 'group' && prp?.possibleFieldTypes?.includes(actionModulerType)) ||
            (prp?.type === 'group' &&
              prp?.items.filter((i) => i?.possibleFieldTypes?.includes(actionModulerType))?.length)
          );
        })
        .map((fprop) => {
          if (fprop?.type === 'group') {
            const grpItems = fprop?.items.filter((i) => i?.possibleFieldTypes?.includes(actionModulerType));
            fprop = { ...fprop, ...{ items: grpItems } };
          }
          return fprop;
        });
    }
    if (properties[actionModulerType]) {
      return properties[actionModulerType].properties;
    }
  }

  getDefaultValues(
    actionModulerType: any,
    filteredDefaultValues: any,
    formDefaultValues,
    allProps,
    nodeTypeField?: any
  ) {
    if (!allProps) {
      return null;
    }
    try {
      if (nodeTypeField !== nodeType.FIELD) {
        actionModulerType = camelCase(actionModulerType);
      }
      if (
        !(
          actionModulerType in filteredDefaultValues &&
          Object.keys(filteredDefaultValues[actionModulerType]).length === 0
        ) &&
        actionModulerType !== '' &&
        actionModulerType !== undefined
      ) {
        const val = this.getPropertyValues(allProps, actionModulerType, nodeTypeField);
        const properties = [...val];
        for (const property of properties) {
          if (property?.type === 'group') {
            for (const groupedProperty of property.items) {
              if (groupedProperty?.uiType === 'urlgroup') {
                console.log('groupedProperty');
              } else {
                this.checkForDefaultValues(groupedProperty, formDefaultValues, actionModulerType);
              }
            }
          } else {
            this.checkForDefaultValues(property, formDefaultValues, actionModulerType);
          }
        }
        filteredDefaultValues[actionModulerType] = formDefaultValues;
      } else {
        formDefaultValues = filteredDefaultValues[actionModulerType];
      }
      return formDefaultValues;
    } catch (e) {
      console.log(e);
    }
  }

  toUpperCase(data) {
    if (!data || !data.toUpperCase) {
      return data;
    }
    return data.toUpperCase();
  }

  isEqualIgnoreCase(data1, data2, ignoreProperties) {
    if (this.toUpperCase(data1) === this.toUpperCase(data2) || data1 === data2) {
      return true;
    }
    if (typeof data1 !== 'object' || typeof data2 !== 'object') {
      return this.toUpperCase(data1) === this.toUpperCase(data2);
    }
    const isValueEmpty1 = data1 === null || data1 === '' || data1 === undefined || $.isEmptyObject(data1);
    const isValueEmpty2 = data2 === null || data2 === '' || data2 === undefined || $.isEmptyObject(data2);
    if (isValueEmpty1 && isValueEmpty2) {
      return true;
    }

    if (isValueEmpty1 && !isValueEmpty2) {
      return false;
    }
    if (!isValueEmpty1 && isValueEmpty2) {
      return false;
    }

    ignoreProperties = ignoreProperties || [];
    let keys = Object.keys(data1 || {});
    const data2Keys = Object.keys(data2 || {});
    keys = keys.concat(Object.keys(data2 || {}));
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index];
      const value1 = this.toUpperCase(data1[key]);
      const value2 = this.toUpperCase(data2[key]);
      if (ignoreProperties.indexOf(key) !== -1) {
        continue;
      }
      if (
        value1 === value2 ||
        ((value1 === null || value1 === '' || value1 === undefined) &&
          (value2 === null || value2 === '' || value2 === undefined))
      ) {
        continue;
      }

      const isValue1Array = Array.isArray(value1);
      const isValue2Array = Array.isArray(value2);
      if (
        (isValue1Array && !isValue2Array) ||
        (!isValue1Array && isValue2Array) ||
        (isValue1Array && isValue2Array && value1.length !== value2.length)
      ) {
        return false;
      }
      let retVal = false;
      if (isValue1Array && isValue2Array) {
        for (let arrIndex = 0; arrIndex < value1.length; arrIndex++) {
          retVal = this.isEqualIgnoreCase(value1[arrIndex], value2[arrIndex], ignoreProperties);
        }
      } else if (typeof value1 === 'object' && typeof value2 === 'object') {
        retVal = this.isEqualIgnoreCase(value1, value2, ignoreProperties);
      } else if (
        (typeof value1 === 'string' && typeof value2 === 'string') ||
        (typeof value1 === 'number' && typeof value2 === 'number')
      ) {
        retVal = value1 === value2;
      }
      if (!retVal) {
        return false;
      }
    }
    return true;
  }

  getDifKeyValObj(data1, data2, ignoreProperties) {
    if (data1 || data2) {
      let keys = Object.keys(data1 || {});
      keys = keys.concat(Object.keys(data2 || {}));
      const diff = Object.keys(data2).reduce((diff, key) => {
        if ((isEqual(data1?.[key], data2[key]) || ignoreProperties.indexOf(key)) > -1) return diff;
        return {
          ...diff,
          [key]: data2[key]
        };
      }, {});
      return diff;
    } 
    return null;

  }

  checkEditPermission(page) {
    try {
      const appId = this.appGlobalService.get('appId');
      const isInsideApp = !!appId && appId !== 'appId';

      // Check if merge is in progress - app should be view-only (only within an app)
      if (isInsideApp && this.appGlobalService.get('isMergeInProgress')) {
        return false;
      }

      if (isInsideApp && this.appGlobalService.get('appRefType') === 'TAG') {
        return false;
      }

      // Branch maintenance status only applies within an app context
      if (isInsideApp && !this.checkBranchMaintenanceStatus()) {
        return false;
      }

      const user = this.currentUser;
      if (user) {
        if (user.sysAdmin || user.devAdmin) {
          return true;
        }
        const orgId = this.appGlobalService.get('orgId');
        if (user.orgInfos) {
          if (user.orgInfos[orgId] === 'Organization Admin') {
            return true;
          }
          if (
            user.devViewer &&
            user.orgInfos[orgId] !== 'Developer' &&
            user.orgInfos[orgId] !== 'Consultant' &&
            user.orgInfos[orgId] !== 'Architect'
          ) {
            return false;
          }
          if (user.devViewer && (user.orgInfos[orgId] === 'Developer' || user.orgInfos[orgId] === 'Architect')) {
            return true;
          }
        }
        const role = this.appGlobalService.get('appRole');
        if (role && (Roles[role].disallowEditScreens.includes(page) || Roles[role].disallowEditScreens.includes('*'))) {
          return false;
        }
        if (!role) {
          if (
            Roles[user.orgInfos[orgId]].disallowEditScreens.includes(page) ||
            Roles[user.orgInfos[orgId]].disallowEditScreens.includes('*')
          ) {
            return false;
          }
          if (user.devViewer) {
            return false;
          }
        }
      }
      return true;
    } catch (e) {
      console.error(e);
    }
    return true;
  }

  // Method to set branchMaintenanceStatus - called from header.component.ts
  branchStatus: string = '';
  private maintenanceDialogOpen: boolean = false;
  private lastShownDialogStatus: string = '';

  /** Resets branch status tracking when a new app context is loaded. */
  resetBranchMaintenanceState(): void {
    this.branchStatus = '';
  }

  setBranchMaintenanceStatus(status: string): void {
    this.branchStatus = status;
    this.showCannotEditMessage();
  }

  // Method to check branch maintenance status before allowing edits
  checkBranchMaintenanceStatus(): boolean {

    if (this.branchStatus === undefined || this.completedStatus.includes(this.branchStatus)) {
      return true;
    } else if (this.blockedStatus.includes(this.branchStatus)) {
      return false;
    }
    return true;
  }

  showCannotEditMessage(): void {
    if (this.appGlobalService.get('appId') === '') {
      return;
    }
    let message: string = '';
    switch (this.branchStatus) {
      case 'BRANCH_CREATION_IN_PROGRESS':
      case 'SUITE_BRANCH_CREATION_IN_PROGRESS':
      case 'BRANCH_VALIDATION_IN_PROGRESS':
        message = this.translate.instant('TARGET_BRANCH_CREATION_IN_PROGRESS');
        break;
      case 'TAG_CREATION_IN_PROGRESS':
      case 'SUITE_TAG_CREATION_IN_PROGRESS':
      case 'TAG_VALIDATION_IN_PROGRESS':
        message = this.translate.instant('TARGET_TAG_CREATION_IN_PROGRESS');
        break;
      case 'BRANCH_CREATION_COMPLETED':
      case 'SUITE_BRANCH_CREATION_COMPLETED':
        message = this.translate.instant('TARGET_BRANCH_CREATION_COMPLETED');
        break;
      case 'TAG_CREATION_COMPLETED':
      case 'SUITE_TAG_CREATION_COMPLETED':
        message = this.translate.instant('TARGET_TAG_CREATION_COMPLETED');
        break;
      case 'BRANCH_CREATION_FAILED':
      case 'SUITE_BRANCH_CREATION_FAILED':
        message = this.translate.instant('TARGET_BRANCH_CREATION_FAILED');
        break;
      case 'TAG_CREATION_FAILED':
      case 'SUITE_TAG_CREATION_FAILED':
        message = this.translate.instant('TARGET_TAG_CREATION_FAILED');
        break;
      default:
        return;
    }
    this.confirmationDialogService.confirm({
      message: message,
      header: 'Information',
      acceptLabel: String(this.translate.instant('OK')),
      rejectVisible: false,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => {
        // Do NOT reset maintenanceDialogOpen here — for the same status, the popup
        // should not appear again. It will re-enable when a different status arrives.
        //reload the current route - clear pending flag for in-progress statuses to bypass canDeactivate guard
        if(['BRANCH_CREATION_IN_PROGRESS', 'TAG_CREATION_IN_PROGRESS','SUITE_BRANCH_CREATION_IN_PROGRESS', 'SUITE_TAG_CREATION_IN_PROGRESS'].includes(this.branchStatus)) {
          this.pendingService.isPending.next(false);
        }
        //reload the current route
        this.reloadRoute();
      }
    });

  }

  reloadRoute(): void {
    const currentUrl = this.router.url;
    const urlParts = currentUrl.split('/');
    if (urlParts.length >= 4 && urlParts[1] === 'home') {
      const baseUrl = urlParts.slice(0, 4).join('/') + '/settings';
      this.router.navigateByUrl(baseUrl, { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(currentUrl);
      });
    } else {
      // Fallback for routes outside home
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(currentUrl);
      });
    }
  }

  getAppUrlForNavigation(appID: string, orgId: string, rappitVersion: string): string {
    if (!appID || !orgId || !rappitVersion) {
      return '';
    }
    if (rappitVersion === environment.rappitVersion) {
      return `${window.location.origin}/#/home/${orgId}/${appID}/gathering/documents`;
    }
    return `${window.location.origin}/${rappitVersion}/#/home/${orgId}/${appID}/gathering/documents`;
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }
  showError(message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: 'error',
      summary: message,
      detail: detail
    });
  }
  showWarning(message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: 'warn',
      summary: message,
      detail: detail
    });
  }
  showInfo(message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: 'info',
      summary: message,
      detail: detail
    });
  }
  /**
   * Indicates popup is already opened or not
   * @param popupOpened
   */
  setPopupRestriction(popupOpened) {
    popupOpened = true;
    setTimeout(() => {
      popupOpened = false; // Reset the flag after the timeout duration
    }, 2000);
  }

  showSuccess(message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: 'success',
      summary: message,
      detail: detail
    });
  }
  isvalidateDMProp(formData, type, val: any, ctrl, isDefFilter?) {
    const errType = isDefFilter ? 'warn' : 'error';
    if (val) {
      // Define constants for days of week to avoid magic numbers
      const SUNDAY = 0;
      const SATURDAY = 6;

      switch (type.toLowerCase()) {
        case 'string':
        case 'text':
        case 'url':
          if (formData.f_aChars) {
            const regExp = new RegExp(formData.f_aChars.trim());
            if (!regExp.exec(val)) {
              this.showMessage(
                errType,
                `The field '${ctrl}' does not meet the 'Regex Pattern’ of '${formData.f_aChars}' configured in the data model.`
              );
              return false;
            }
          }
          if (formData.f_minL && val.length < formData.f_minL && type !== 'url') {
            this.showMessage(
              errType,
              `The field '${ctrl}' does not meet the ‘Minimum Length' of '${formData.f_minL}' configured in the data model.`
            );
            return false;
          }
          if (formData.f_maxL && val.length > formData.f_maxL && type !== 'url') {
            this.showMessage(
              errType,
              `The field '${ctrl}' does not meet the Maximum Length of '${formData.f_maxL}' configured in the data model.`
            );
            return false;
          }
          break;
        case 'number':
        case 'currency':
        case 'double':
          if (formData.f_minVal && val < formData.f_minVal) {
            this.showMessage(
              errType,
              `The value assigned to the '${ctrl}' field does not fall within the defined 'Minimum Value’ (${formData.f_minVal}) in the data model.`
            );
            return false;
          }
          if (formData.f_maxVal && val > formData.f_maxVal) {
            this.showMessage(
              errType,
              `The value assigned to the '${ctrl}' field does not fall within the defined 'Maximum Value’ (${formData.f_maxVal}) in the data model.`
            );
            return false;
          }
          if (isDefFilter && (type.toLowerCase() === 'double' || type.toLowerCase() === 'currency')) {
            const digits = val?.toString()?.split('.')[1];
            if (digits) {
              if (formData.f_maxFrac && digits.length > formData.f_maxFrac) {
                this.showMessage(
                  errType,
                  `The value assigned to the '${ctrl}' field does not fall within the defined 'Maximum Fraction Digits (${formData.f_maxFrac}) in the data model.`
                );
                return false;
              }
            }
          }
          break;
        case 'date':
        case 'datetime':
          if (typeof val !== 'string') {
            const day = new Date(val)?.getDay();
            if (formData.f_allowWeekDay) {
              if ([SUNDAY, SATURDAY].includes(day)) {
                this.showMessage(errType, `${ctrl} Date should contain only weekday`);
                return false;
              }
            }
            if (formData.f_weekEndsOnly) {
              if (![SUNDAY, SATURDAY].includes(day)) {
                this.showMessage(errType, `${ctrl} Date should contain only weekend`);
                return false;
              }
            }

            if (formData.f_minDate) {
              const date1 = new Date(val).getTime();
              const date2 = new Date(formData.f_minDate).getTime();
              if (date1 < date2) {
                this.showMessage(
                  errType,
                  `The value assigned to the '${ctrl}' field does not fall within the defined 'Minimum Value’ (${this.datePipe.transform(new Date(formData.f_minDate), 'dd-MM-yyyy')}) in the data model.`
                );
                return false;
              }
            }
            if (formData.f_maxDate) {
              const date1 = new Date(val).getTime();
              const date2 = new Date(formData.f_maxDate).getTime();
              if (date1 > date2) {
                this.showMessage(
                  errType,
                  `The value assigned to the '${ctrl}' field does not fall within the defined 'Maximum Value’ (${this.datePipe.transform(new Date(formData.f_maxDate), 'dd-MM-yyyy')}}) in the data model.`
                );
                return false;
              }
            }
          }
          break;
        case 'email': {
          const reg = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/g;
          if (!reg.exec(val)) {
            this.showMessage(errType, `${ctrl} for Email is invalid`);
            return false;
          }
          break;
        }
        default:
          break;
      }
    }
    return true;
  }

  public getFormattedFirstName(firstName) {
    if (!firstName || !firstName.split) {
      return '';
    }
    const splittedName = firstName.split('-');
    let formattedName = '';
    for (let index = 0; index < splittedName.length; index++) {
      if (splittedName[index] === '') {
        continue;
      }
      formattedName += `-${splittedName[index].charAt(0).toUpperCase()}${splittedName[index].slice(1)}`;
    }

    return formattedName.slice(1);
  }

  getNameObjectFromEmail = function (email) {
    if (!email || !email.split) {
      return '';
    }
    const split = email.split('@');
    const splitName = split[0];
    const names = splitName.split('.');

    const name: any = {};
    name.firstname = this.getFormattedFirstName(names[0]);
    name.lastname = names[1] ? names[1].toUpperCase() : '';
    return `${name.firstname} ${name.lastname}`;
  };

  private buildAdditionalInfo(adInfo: any, path: string | undefined, propertyName: string | undefined): any {
    let additionalInfo: any = null;

    if (adInfo) {
      const clonedInfo = { ...adInfo };
      if (propertyName === 'PAGE_FIELDS' && Object.prototype.hasOwnProperty.call(clonedInfo, 'detailPageId')) {
        delete clonedInfo.detailPageId;
      }
      additionalInfo = { ...clonedInfo };
    }

    if (path) {
      additionalInfo = additionalInfo ? { path: path, ...additionalInfo } : { path: path };
    }

    if (additionalInfo?.parentElementType === 'captionItem') {
      additionalInfo.parentElementType = 'formField';
      if (typeof additionalInfo?.path === 'string') {
        additionalInfo.path = additionalInfo.path.replace('DETAIL_VIEW/CAPTION_BAR/', 'DETAIL_VIEW/FORM/');
      }
    }

    return additionalInfo;
  }

  createReference(
    modelId,
    modelType,
    referredModelId,
    referredModelType,
    referredAppId,
    path?,
    propertyName?,
    manualChange?,
    adInfo?,
    sourceFieldId?,
    msAppId?,
    configurationId?
  ) {
    const appId = msAppId ? msAppId : this.appGlobalService.get('appId');
    referredAppId = referredAppId ? referredAppId : appId;
    const sid = this.constructSidForRef({
      modelId: modelId,
      modelType: modelType,
      referredModelId: referredModelId,
      referredModelType: referredModelType,
      propertyName: propertyName,
      appId: appId,
      referredAppId: referredAppId,
      manualChange: manualChange,
      sourceFieldId: sourceFieldId,
      path: path,
      adInfo: adInfo,
      configurationId: configurationId
    });
    let obj: any = {
      modelId: modelId,
      modelType: modelType,
      referredModelId: referredModelId,
      referredModelType: referredModelType,
      propertyName: propertyName,
      appId: appId,
      orgId: this.appGlobalService.get('orgId'),
      referredAppId: referredAppId,
      manualChange: manualChange,
      sid: sid
    };

    const additionalInfo = this.buildAdditionalInfo(adInfo, path, propertyName);
    if (additionalInfo) {
      obj = { ...obj, additionalInfo };
    }
    return obj;
  }

  constructSidForRef(obj) {
    // Base SID structure: appid_$_modelId_$_RefAppId_$_RefModelId_$_PropName
    let sid = `${obj.appId}_$_${obj.modelId}_$_${obj.referredAppId}_$_${obj.referredModelId}`;
    sid = this.appendPropertyNameToSid(sid, obj);

    // Reference configuration mapping with additional info requirements and SID patterns
    const referenceConfig: Record<
      string,
      { additionalInfo?: string[]; appendNodeId?: boolean; appendSourceFieldId?: boolean }
    > = {
      // Properties with path and menu in additionalInfo, append node.data.id to SID
      PAGE_ACTION: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      APIGROUP_SERVICE_BUTTON: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      SERVICE: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      BUTTON_VISIBILITY_CONDITIONAL: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      BUTTON_ENABLED_CONDITIONAL: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      EDIT_CONDITIONAL: {
        additionalInfo: ['path', 'menu', 'parentElementId', 'parentElementType'],
        appendNodeId: true
      },
      VIEW_CONDITIONAL: {
        additionalInfo: ['path', 'menu', 'parentElementId', 'parentElementType'],
        appendNodeId: true
      },
      NAVIGATION_DATA_MAPPING_SOURCE_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'menu', 'parentElementId', 'parentElementType'],
        appendNodeId: true
      },
      NAVIGATION_DATA_MAPPING_TARGET_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'sourceFieldId', 'menu', 'parentElementId', 'parentElementType'],
        appendNodeId: true,
        appendSourceFieldId: true
      },
      HOLD_VALUE_PARAMS_MAPPING_SOURCE_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'parentElementId', 'parentElementType', 'menu'],
        appendNodeId: true
      },
      HOLD_VALUE_PARAMS_MAPPING_TARGET_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'parentElementId', 'parentElementType', 'sourceFieldId', 'menu'],
        appendNodeId: true,
        appendSourceFieldId: true
      },
      DETAIL_VIEW_PARAMS_MAPPING_SOURCE_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'parentElementId', 'parentElementType', 'menu'],
        appendNodeId: true
      },
      DETAIL_VIEW_PARAMS_MAPPING_TARGET_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'parentElementId', 'parentElementType', 'sourceFieldId', 'menu'],
        appendNodeId: true,
        appendSourceFieldId: true
      },
      PAGE_FIELDS: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      LOOKUP_TO_QUERYVIEW: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'menu'],
        appendNodeId: true
      },
      PAGES_INPUT_MAPPING_SOURCE_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'queryviewId', 'lookupDefinitionId', 'menu'],
        appendNodeId: true
      },
      PAGES_INPUT_MAPPING_TARGET_FIELD: {
        additionalInfo: [
          'path',
          'parentElementId',
          'parentElementType',
          'queryviewId',
          'lookupDefinitionId',
          'sourceFieldId',
          'menu'
        ],
        appendNodeId: true,
        appendSourceFieldId: true
      },
      PAGES_OUTPUT_MAPPING_SOURCE_FIELD: {
        additionalInfo: [
          'path',
          'parentElementId',
          'parentElementType',
          'queryviewId',
          'foreignKeyId',
          'lookupDefinitionId',
          'menu'
        ],
        appendNodeId: true
      },
      PAGES_OUTPUT_MAPPING_TARGET_FIELD: {
        additionalInfo: [
          'path',
          'parentElementId',
          'parentElementType',
          'queryviewId',
          'foreignKeyId',
          'lookupDefinitionId',
          'sourceFieldId',
          'menu'
        ],
        appendNodeId: true,
        appendSourceFieldId: true
      },
      EXECUTE_ON_FIELD_MAPPING: {
        additionalInfo: [
          'path',
          'parentElementId',
          'parentElementType',
          'queryviewId',
          'foreignKeyId',
          'lookupDefinitionId',
          'menu'
        ],
        appendNodeId: true
      },
      EXECUTE_ON_PAGE_VARIABLE_MAPPING: {
        additionalInfo: [
          'path',
          'parentElementId',
          'parentElementType',
          'queryviewId',
          'foreignKeyId',
          'lookupDefinitionId',
          'menu'
        ],
        appendNodeId: true
      },
      LOOKUP_DEFINITION: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'queryviewId', 'foreignKeyId', 'menu'],
        appendNodeId: true
      },
      ACCESS_CONTROL: { additionalInfo: ['path', 'menu'], appendNodeId: true },
      GRID_COLUMN_NAVIGATION_PAGE: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'menu'],
        appendNodeId: true
      },
      NAVIGATION_PAGE: { additionalInfo: ['path', 'menu'], appendNodeId: true },

      // Properties with path only in additionalInfo
      DEFAULT_SORT_ON: { additionalInfo: ['path'] },
      GROUPS_ON_COLUMN: { additionalInfo: ['path'] },
      ALIGN_COLUMNS: { additionalInfo: ['path'] },
      DEFAULT_FILTER: { additionalInfo: ['path'] },
      TABLE_SEARCH: { additionalInfo: ['path'] },
      QUICK_FILTERS: { additionalInfo: ['path'] },
      TABLE: { additionalInfo: ['path'] },
      FORM: { additionalInfo: ['path'] },
      STANDARD_DATAGRID: { additionalInfo: ['path'] },
      QUERY_VIEW: { additionalInfo: ['path'] },
      CHILD_PAGE: { additionalInfo: ['path'] },
      CAPTION_BAR: { additionalInfo: ['path'] },
      LOOKUPTO_FOREIGNKEY: { additionalInfo: ['path', 'parentElementId', 'parentElementType'] },
      NAVIGATION_DETAIL_PAGE: { additionalInfo: ['path'] },

      // Properties with path and detailPageId
      DETAIL_PAGE_MAPPING_SOURCE_FIELD: { additionalInfo: ['path', 'detailPageId'] },
      DETAIL_PAGE_MAPPING_TARGET_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'sourceFieldId'],
        appendSourceFieldId: true
      },
      DETAIL_PAGE_PARAMETERS_SOURCE_FIELD: { additionalInfo: ['path', 'detailPageId'] },
      DETAIL_PAGE_PARAMETERS_TARGET_FIELD: {
        additionalInfo: ['path', 'detailPageId', 'sourceFieldId'],
        appendSourceFieldId: true
      },

      // Properties with path and parentElementId/parentElementType
      QUERY_VIEW_MAPPING_SOURCE_FIELD: { additionalInfo: ['path', 'parentElementId', 'parentElementType'] },
      QUERY_VIEW_MAPPING_TARGET_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'sourceFieldId'],
        appendSourceFieldId: true
      },
      QUERY_VIEW_MAPPING_PAGE_VARIABLES_SOURCE_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType']
      },
      QUERY_VIEW_MAPPING_PAGE_VARIABLES_TARGET_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'sourceFieldId'],
        appendSourceFieldId: true
      },
      STANDARD_GRID_PARAMETER_MAPPING_PAGE_VARIABLES_SOURCE_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType']
      },
      STANDARD_GRID_PARAMETER_MAPPING_PAGE_VARIABLES_TARGET_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'sourceFieldId'],
        appendSourceFieldId: true
      },

      // Properties with path and menu
      PARAMETER_MAPPING_QUERY_VIEW_SOURCE_FIELD: { additionalInfo: ['path', 'menu'] },
      PARAMETER_MAPPING_QUERY_VIEW_TARGET_FIELD: {
        additionalInfo: ['path', 'sourceFieldId', 'menu'],
        appendSourceFieldId: true
      },
      PARAMETER_MAPPING_SOURCE_FIELD: { additionalInfo: ['path', 'parentElementId', 'parentElementType', 'menu'] },
      PARAMETER_MAPPING_TARGET_FIELD: {
        additionalInfo: ['path', 'parentElementId', 'parentElementType', 'sourceFieldId', 'menu'],
        appendSourceFieldId: true
      },

      // Properties with no path in additionalInfo
      WORKFLOW_HISTORY_BUTTON: { additionalInfo: [] },
      QUERY_VIEW_MULTIPLE_DETAIL_PAGE: { additionalInfo: [] },
      QUERY_PAGE: { additionalInfo: [] },
      TABLE_PAGE: { additionalInfo: [] },

      // Query Builder rule references (SID needs sourceFieldId appended per spec)
      RULE_TARGET_FIELD: { additionalInfo: [], appendSourceFieldId: true },
      RULE_FIELD_VALUE: { additionalInfo: [], appendSourceFieldId: true },
      DEFAULT_FILTER_PAGE_VARIABLE: { additionalInfo: [], appendSourceFieldId: true }
    };

    const config = referenceConfig[obj.propertyName];
    sid = this.applyConfigBasedSidExtensions(sid, config, obj);
    sid = this.appendForeignKeyAndQueryViewConfig(sid, obj);

    return sid;
  }

  private appendPropertyNameToSid(sid: string, obj): string {
    if (!obj.propertyName) {
      return sid;
    }
    sid = `${sid}_$_${obj.propertyName}`;
    if (obj.propertyName.includes('CONDITIONAL') && obj.path) {
      const buttonId = obj.path.split('/').pop();
      sid = `${sid}_$_${buttonId}`;
    }
    return sid;
  }

  private applyConfigBasedSidExtensions(sid: string, config, obj): string {
    if (!config) {
      return sid;
    }
    // Append sourceFieldId to SID if required
    if (config.appendSourceFieldId && obj.sourceFieldId && !sid.endsWith(`_$_${obj.sourceFieldId}`)) {
      sid = `${sid}_$_${obj.sourceFieldId}`;
    }
    // Append node.data.id (menu) to SID if required
    if (config.appendNodeId && obj.adInfo?.menu && !sid.endsWith(`_$_${obj.adInfo.menu}`)) {
      sid = `${sid}_$_${obj.adInfo.menu}`;
    }
    return sid;
  }

  private appendForeignKeyAndQueryViewConfig(sid: string, obj): string {
    // Append foreignKeySid if provided (for special cases like referredTableFields)
    if (obj.foreignKeySid) {
      sid = `${sid}_$_${obj.foreignKeySid}`;
    }
    // Append configurationId for query view related properties
    const queryViewProps = [
      'QUERY_VIEW',
      'QUERY_VIEW_MAPPING_SOURCE_FIELD',
      'QUERY_VIEW_MAPPING_TARGET_FIELD',
      'QUERY_VIEW_MAPPING_PAGE_VARIABLES_TARGET_FIELD',
      'QUERY_VIEW_MAPPING_PAGE_VARIABLES_SOURCE_FIELD',
      'PARAMETER_MAPPING_QUERY_VIEW_SOURCE_FIELD',
      'PARAMETER_MAPPING_QUERY_VIEW_TARGET_FIELD'
    ];
    if (obj.configurationId && queryViewProps.includes(obj.propertyName)) {
      sid = `${sid}_$_${obj.configurationId}`;
    }
    return sid;
  }

  createObjForDelRef(modelId, referredModelId, propertyName?, adInfo?, sourceFieldId?, referredAppId?, configurationId?) {
    const appId = this.appGlobalService.get('appId');

    // Construct SID for deletion to match the creation pattern
    const sid = this.constructSidForRef({
      modelId: modelId,
      referredModelId: referredModelId,
      propertyName: propertyName,
      appId: appId,
      referredAppId: referredAppId || appId,
      sourceFieldId: sourceFieldId,
      adInfo: adInfo,
      configurationId: configurationId
    });

    const obj: any = {
      modelId: modelId,
      referredModelId: referredModelId,
      propertyName: propertyName,
      sid: sid
    };

    if (adInfo) {
      obj.additionalInfo = adInfo;
    }

    return obj;
  }

  getNameFromAppMetaInfo(id) {
    if (id.indexOf('@') > -1) {
      return id ? this.getNameObjectFromEmail(id.match(/^([^@]*)@/)[1]) : '';
    } else if (id && id.toLowerCase().indexOf('system') > -1) {
      return 'System';
    }
    const d = this.appGlobalService.get('appmetaInfo') || {};
    return d[id] ? d[id] : '';
  }

  getNameUsingMetaInfo(metaInfo: any, id: string) {
    return id && metaInfo[id]
      ? metaInfo[id]
      : this.refAppMetaInfos && this.refAppMetaInfos[id]
        ? this.refAppMetaInfos[id]
        : '';
  }

  formReplacementsObj(d: any, item?: any) {
    return {
      '{referredModel}': d.referredModelId,
      '{model}': d.modelId,
      '{table}': d.tableId,
      '{field}': d.fieldId,
      '{step}': d.step,
      '{actor}': d.actor,
      '{action}': d.action,
      '{configuredFieldName}': d.configuredFieldName,
      '{workflow}': d.workflow,
      '{workflowActor}': d.workflowActor,
      '{item}': item?.name || '',
      '{itemTable}': item?.table,
      '{type}': d.type,
      '{tableName}': d.tableName,
      '{templateName}': d.templateName,
      '{parentTableName}': d.parentTableName,
      '{EnvironmentName}': d.EnvironmentName,
      '{fieldName}': d.fieldName,
      '{workflowAction}': d.workflowAction,
      '{workflowStep}': d.workflowStep,
      '{pageName}': d.pageName,
      '{environmentName}': d.environmentName,
      '{homeTileName}': d.homeTileName,
      '{tabItemName}': d.tabItemName,
      '{workflowActionStep}': d.workflowActionStep,
      '{workflowName}': d.workflowName,
      '{menuItemName}': d.menuItemName,
      '{mappedFieldName}': d.mappedFieldName,
      '{fieldType}': d.fieldType,
      '{buttonName}': d.buttonName,
      '{customButtonName}': d.customButtonName,
      '{tableAclName}': d.tableAclName,
      '{fieldToMap}': d.fieldToMap,
      '{fieldToRemove}': d.fieldToRemove,
      '{roleName}': d.roleName,
      '{actionName}': d.actionName,
      '{secondCompsiteTableName}': d.secondCompsiteTableName,
      '{serviceName}': d.serviceName,
      '{providerName}': d.providerName,
      '{APIName}': d.APIName,
      '{fileName}': d.fileName,
      '{fKeyName}': d.fKeyName,
      '{propertyValue}': d.propertyValue,
      '{operator}': d.operator,
      '{propertyName}': d.propertyName,
      '{value}': d.value,
      '{defaultFilterName}': d.defaultFilterName,
      '{childTableName}': d.childTableName,
      '{childFieldName}': d.childFieldName,
      '{qvTableName}': d.qvTableName,
      '{qvFieldName}': d.qvFieldName,
      '{size}': d.size,
      '{queryViewPage}': d.queryViewPage,
      '{aliasSid}': d.aliasSid,
      '{staticInput}': d.staticInput,
      '{foreignKey}': d.foreignKey,
      '{sectionName}': d.sectionName,
      '{lookupFieldNa me}': d.lookupFieldName,
      '{variableName}': d.variableName,
      '{gridColumn}': d.gridColumn,
      '{buttonPath}': d.buttonPath,
      '{duplicateQueryStringNames}': d.duplicateQueryStringNames,
      '{maxLengthExceededQueryStrings}': d.maxLengthExceededQueryStrings,
      '{queryStringsWithMaxLimit}': d.queryStringsWithMaxLimit,
      '{queryStringsWithCircularDependency}': d.queryStringsWithCircularDependency,
      '{missingQueryStringReferences}': d.missingQueryStringReferences,
      '{duplicateDataKey}': d.duplicateDataKey,
      '{elementsList}': d.elementsList,
      '{duplicateDetailPageFields}': d.duplicateDetailPageFields,
      '{duplicateListPageFields}': d.duplicateListPageFields,
      '{undeclaredParams}': d.undeclaredParams,
      '{unusedFilters}': d.unusedFilters,
      '{indexName1}': d.indexName1,
      '{indexName2}': d.indexName2,
      '{indexSid}': d.indexSid,
      '{aliasName}': d.aliasName,
      '{primaryIds}': d.primaryIds,
      '{uniqueIds}': d.uniqueIds,
     

    };
  }

  snakeToTitleCase(snakeCase: string) {
    const sentence = snakeCase.toLowerCase();
    return sentence
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Normalize display names: convert all-uppercase strings (e.g. "SYSTEM") to title case ("System").
   * Returns the original string unchanged if it is not all-uppercase.
   */
  normalizeDisplayName(name: string): string {
    if (!name || name.length <= 1) return name;
    if (name === name.toUpperCase()) {
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    }
    return name;
  }

  formatComponentType(componentType: string | undefined): string {
    if (!componentType) return '';
    if (componentType === 'APIGROUP') {
      return this.translate.instant('FUNC_COMP_CONSUMED_FROM');
    }
    if (componentType === 'EXPOSURE') {
      return this.translate.instant('EXPOSURE');
    }
    if (componentType === 'CONSUMER') {
      return this.translate.instant('CONSUMER');
    }
    if (componentType === 'FUNC_COMP') {
      return this.translate.instant('FUNC_COMP_EXPOSED_TO');
    }
    return componentType
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  compareStrings(a: string, b: string) {
    const data1 = a.replace(/[_\s]/g, '').toLowerCase();
    const data2 = b.replace(/[_\s]/g, '').toLowerCase();
    return data1 === data2;
  }

  enrichAdInfo(
    adInfo: Record<string, unknown> | undefined,
    params: {
      props?: { currentNode?: string };
      node?: { type?: string };
      detailPageId?: string;
      lookupConfig?: { lookupToQueryView?: unknown; lookupDefinition?: unknown; lookupToForeignkey?: unknown };
    }
  ): Record<string, unknown> | undefined {
    if (!adInfo) {
      return adInfo;
    }
    const { props, node, detailPageId, lookupConfig } = params || {};
    if (props && props.currentNode) {
      adInfo.parentElementId = props.currentNode;
    }
    if (node && node.type) {
      adInfo.parentElementType = node.type;
    }
    if (detailPageId) {
      adInfo.detailPageId = detailPageId;
    }
    const newlk: any = lookupConfig; // existing codebase uses dynamic lookup configuration shape
    if (newlk?.lookupDefinition) {
      adInfo.lookupDefinitionId = newlk.lookupDefinition;
    }
    if (newlk?.lookupToForeignkey) {
      adInfo.foreignKeyId = newlk.lookupToForeignkey;
    }
    if (newlk?.lookupToQueryView) {
      adInfo.queryViewId = newlk.lookupToQueryView;
    }
    return adInfo;
  }
  updateAdInfoForLookup(adInfo: Record<string, unknown> | undefined, propName: string, mapName?: string) {
    if (!adInfo || !propName) {
      return adInfo;
    }
    let newAdinfo: Record<string, unknown> = {};

    newAdinfo = this.buildBaseLookupAdInfo(newAdinfo, adInfo, propName);
    newAdinfo = this.buildMappingAdInfo(newAdinfo, adInfo, propName, mapName);
    newAdinfo = this.appendMenuIfNeeded(newAdinfo, adInfo, propName, mapName);

    return newAdinfo;
  }

  private buildBaseLookupAdInfo(
    newAdinfo: Record<string, unknown>,
    adInfo: Record<string, unknown>,
    propName: string
  ): Record<string, unknown> {
    if (propName === 'LOOKUP_TO_QUERYVIEW') {
      return {
        ...newAdinfo,
        ...(adInfo?.path ? { path: adInfo.path } : {}),
        parentElementId: adInfo?.parentElementId,
        parentElementType: adInfo?.parentElementType,
        ...(adInfo?.menu ? { menu: adInfo.menu } : {})
      };
    }
    if (propName === 'LOOKUP_TO_FOREIGNKEY') {
      return {
        ...newAdinfo,
        ...(adInfo?.path ? { path: adInfo.path } : {}),
        parentElementId: adInfo?.parentElementId,
        parentElementType: adInfo?.parentElementType
      };
    }
    if (propName === 'LOOKUP_DEFINITION') {
      return {
        ...newAdinfo,
        parentElementId: adInfo?.parentElementId,
        parentElementType: adInfo?.parentElementType,
        ...this.getForeignKeyOrQueryViewId(adInfo),
        ...(adInfo?.menu ? { menu: adInfo.menu } : {})
      };
    }
    return newAdinfo;
  }

  private buildMappingAdInfo(
    newAdinfo: Record<string, unknown>,
    adInfo: Record<string, unknown>,
    propName: string,
    mapName?: string
  ): Record<string, unknown> {
    const isMappingContext = mapName === 'inputMapping' || mapName === 'outputMapping' || propName === 'EXECUTE_ON_MAPPING';
    if (!isMappingContext) {
      return newAdinfo;
    }
    newAdinfo = {
      ...newAdinfo,
      ...(adInfo?.path ? { path: adInfo.path } : {}),
      parentElementId: adInfo?.parentElementId,
      parentElementType: adInfo?.parentElementType,
      ...this.getForeignKeyOrQueryViewId(adInfo),
      lookupDefinitionId: adInfo?.lookupDefinitionId
    };
    if (propName === 'tableField') {
      newAdinfo = {
        ...newAdinfo,
        ...(adInfo?.sourceFieldId ? { sourceFieldId: adInfo.sourceFieldId } : {})
      };
    }
    return newAdinfo;
  }

  private appendMenuIfNeeded(
    newAdinfo: Record<string, unknown>,
    adInfo: Record<string, unknown>,
    propName: string,
    mapName?: string
  ): Record<string, unknown> {
    const needsMenu =
      mapName === 'inputMapping' ||
      mapName === 'outputMapping' ||
      propName === 'EXECUTE_ON_MAPPING' ||
      propName === 'LOOKUP_DEFINITION';
    if (needsMenu) {
      return {
        ...newAdinfo,
        ...(adInfo?.menu ? { menu: adInfo.menu } : {})
      };
    }
    return newAdinfo;
  }

  private getForeignKeyOrQueryViewId(adInfo: Record<string, unknown>): Record<string, unknown> {
    if (adInfo?.foreignKeyId) {
      return { foreignKeyId: adInfo.foreignKeyId };
    }
    if (adInfo?.queryViewId) {
      return { queryViewId: adInfo.queryViewId };
    }
    return {};
  }

  updateAdInfoForMapping(adInfo: Record<string, unknown> | undefined, propName: string, mapName?: string) {
    if (!adInfo || !propName) {
      return adInfo;
    }
    let newAdinfo: Record<string, unknown> = {};
    const parentMapNames = [
      'queryViewMapping',
      'parameterMapping',
      'detailViewParams',
      'parameterMappingPageVariables',
      'queryViewMappingPageVariables',
      'holdValueParams',
      'navigatePageValueMapping'
    ];

    const detailMapNames = [
      'detailPageMapping',
      'detailViewParams',
      'detailPageParameters',
      'holdValueParams',
      'navigatePageValueMapping'
    ];

    const menuMapNames = [
      'parameterMapping',
      'holdValueParams',
      'parameterMappingForQueryList',
      'detailViewParams',
      'gridColumnDetailPage',
      'navigatePageValueMapping'
    ];

    if (parentMapNames.includes(mapName || ``) || propName === 'gridColumnDetailPage') {
      newAdinfo = {
        ...newAdinfo,
        parentElementId: adInfo?.parentElementId,
        parentElementType: adInfo?.parentElementType
      };
    }
    if (detailMapNames.includes(mapName || ``)) {
      newAdinfo = {
        ...newAdinfo,
        detailPageId: adInfo?.detailPageId
      };
    }
    if (['detailField', 'tableField', 'targetKey'].includes(propName)) {
      newAdinfo = {
        ...newAdinfo,
        ...(adInfo?.sourceFieldId ? { sourceFieldId: adInfo.sourceFieldId } : {})
      };
    }
    if (menuMapNames.includes(mapName || ``) || propName === 'gridColumnDetailPage') {
      newAdinfo = {
        ...newAdinfo,
        ...(adInfo?.menu ? { menu: adInfo.menu } : {})
      };
    }
    if (Object.entries(newAdinfo).length === 0 && propName !== 'detailPage') {
      return adInfo;
    }

    return newAdinfo;
  }

  allowedValuesSpecialCharsMatchCheck(values: any) {
    try {
      if (/[\\""]/g.test(values)) {
        return true;
      }
      return false;
    } catch (e: any) {
      console.log(e);
      return false;
    }
  }

  getAppUserTable() {
    this.bs.get(ApiConstants.getAppTableID).subscribe({
      next: (response: any) => {
        this.appGlobalService.write('appTableId', response[0]?.sid);
      },
      error: () => console.info('error occurred')
    });
  }

  convertTableName(name) {
    name = name.replace(/\s/g, '');
    return name.charAt(0).toLowerCase() + name.slice(1);
  }

  formMapForParFld(arr, fld) {
    if (arr.length && fld) {
      // filter the values which are not null
      return fld
        ?.map((df) => {
          const acc = arr.find((f) => f.sid === df);
          return acc
            ? {
                sid: acc.sid,
                name: acc.name,
                f_type: acc.f_type,
                tableId: acc.f_tableId,
                appId: acc.appId === this.appGlobalService.get('appId') ? null : acc.appId
              }
            : null;
        })
        .filter((fd) => fd);
    }
    return null;
  }

  /**
   * Capitalizes the first character of a given string.
   *
   * @param {string} value - The input string to be transformed.
   * @returns {string} - The transformed string with the first character capitalized.
   *
   * @example
   * // Returns 'Hello'
   * capitalizeFirstChar('hello');
   */
  capitalizeFirstChar(value: string): string {
    if (!value) {
      return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /**
   * Validates an XML string by parsing it into an XMLDocument and checking for errors.
   *
   * @param {string} xml - The XML string to be validated.
   * @returns {string} - The formatted XML string if it is valid.
   * @throws {Error} - Throws an error if the XML string contains parsing errors.
   *
   * Example usage:
   * ```typescript
   * try {
   *     const validXML = validateXML('<Item><id>1</id><name>Sample Item</name></Item>');
   *     console.log('Valid XML:', validXML);
   * } catch (error) {
   *     console.error('Invalid XML:', error.message);
   * }
   * ```
   */
  validateXML(xml: string): string {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, 'application/xml');

    // Check for any parsing errors
    const parseError = xmlDoc.getElementsByTagName('parsererror');
    if (parseError.length > 0) {
      throw new Error(`Error parsing XML: ${parseError[0].textContent}`);
    }
    return this.formatXML(xml);
  }

  /**
   * Formats an XML string with proper indentation using the xml-formatter library.
   *
   * @param {string} xml - The XML string to be formatted.
   * @returns {string} - The formatted XML string.
   *
   * Example usage:
   * ```typescript
   * const xmlString = '<root><child>value</child></root>';
   * const formattedXML = formatXML(xmlString);
   * console.log(formattedXML);
   * ```
   */
  formatXML(xml: string): string {
    return xmlFormatter(xml, {
      indentation: '  ', // String to use for indentation (default is 2 spaces)
      filter: (node) => node.type !== 'Comment', // Function to filter nodes
      collapseContent: true, // Collapses text content to a single line
      lineSeparator: '\n' // String to use for new lines
    });
  }

  filterFieldsBasedOnConstants(constName, flds) {
    return flds.filter((f) => {
      const obj = AllowedFieldsConstants[constName];
      const conds = Object.keys(obj);
      const condMatch = conds.filter((c) => {
        if (Array.isArray(obj[c])) {
          if (!obj[c].includes(f[c]?.toLowerCase())) {
            return true;
          }
        } else {
          if (typeof obj[c] === 'string' && f[c] === obj[c]) {
            return true;
          }
          if (typeof obj[c] === 'boolean' && f[c] !== obj[c]) {
            return true;
          }
        }
        return false;
      });
      if (condMatch.length === conds.length) {
        return true;
      }
      return false;
    });
  }

  isRestrictedFieldByConstants(constName: string, field?: any): boolean {
    if (!field) {
      return false;
    }
    return this.filterFieldsBasedOnConstants(constName, [field]).length === 0;
  }

  public static documentLinkTemplate = (linkText: string): string => {
    return `<a href='${GlobalConstants.documentLinkKeyword}' target="_blank">${linkText}</a>`;
  };
  /**
   * To show the keyword warning message
   * @param type The type of keyword for which warning to be given
   * @param name The specific name type(table, field, field group..) for which the warning is thrown
   * @returns The translated message warning shown
   */

  showKeywordWarning(type: string, name: string): string {
    const linkText = type === 'Sql Keyword' ? 'Sql Keyword' : 'Reserved Keyword';
    const link = UtilService.documentLinkTemplate(linkText);
    const params = {
      name: name,
      keyword: link
    };

    const messageKey = type === 'Sql Keyword' ? 'SQL_KEYWORD_WARN' : 'KEYWORD_WARN';
    const translatedMessage = this.translate.instant(messageKey, params);
    return translatedMessage;
  }

  /**
   * display Error message based on concurrency error code
   * @param data.apiErr // error object from api
   * @param data.admData // application datamodel data
   */
  showErrMsg(data) {
    const error = data?.apiErr?.error;
    let errMsg = error?.MESSAGE;
    const admData = data.admData;
    const tableMap = data.tableMap;
    const fieldMap = data.fieldMap;
    if (error && Object.keys(GlobalConstants.concurrencyErrorMap).includes(error?.ERROR_CODE)) {
      errMsg = this.translate.instant(GlobalConstants.concurrencyErrorMap[error?.ERROR_CODE]);
      if (admData) {
        if (errMsg.includes('tableName') && errMsg.includes('fieldName')) {
          errMsg = errMsg.replace('{tableName}', tableMap[admData.f_tableId]);
          errMsg = errMsg.replace('{fieldName}', fieldMap[admData.sid]);
        } else if (errMsg.includes('fieldgroupName') && errMsg.includes('tableName')) {
          errMsg = errMsg.replace('{tableName}', tableMap[admData.f_tableId]);
          errMsg = errMsg.replace('{fieldgroupName}', fieldMap[admData.pNode]);
        } else if (errMsg.includes('{tableName}')) {
          const tblNme = admData.type === nodeType.TABLE ? tableMap[admData.sid] : tableMap[admData.f_tableId];
          errMsg = errMsg.replace('{tableName}', tblNme);
        } else if (errMsg.includes('{tablegroupName}')) {
          const tblNme = admData.type === nodeType.TABLE ? tableMap[admData.pNode] : tableMap[admData.sid];
          errMsg = errMsg.replace('{tablegroupName}', tblNme);
        }
      }
    }
    if (errMsg) {
      this.showMessage('error', errMsg);
    }
  }

  validateTableName(
    name,
    messages,
    isRdbms?,
    shouldDisplayToast = true,
    isAliasValidation = false,
    validationField = 'Table name'
  ) {
    const title = name.trim().replace(/\s\s+/g, ' ');
    const array = this.tableNameRegex.exec(title);
    const titleToCheck = title.toLowerCase();

    if (!name) {
      if (isAliasValidation) {
        messages.name = this.translate.instant('ALIAS_NAME_REQUIRED');
      } else {
        messages.name = this.translate.instant('TABLE_NAME_REQUIRED');
      }
    } else if (title.length > this.titleMaxLength) {
      messages.name = this.translate.instant('NAME_MAX_LENGTH_ERROR', { maxNo: this.titleMaxLength });
    } else if (!array) {
      messages.name = this.translate.instant('ALPHANUMEREIC_CHARACTERS_ALLOWED');
    } else if (this.getRestrictedKeywords().includes(titleToCheck)) {
      const translatedMessage = this.showKeywordWarning('Reserved Keyword', validationField);
      messages.name = translatedMessage;
      if (shouldDisplayToast) {
        this.showMessage('error', translatedMessage);
      }
    } else if (isRdbms && this.getRestrictedSQLKeywords().includes(titleToCheck)) {
      const translatedMessage = this.showKeywordWarning('Reserved Keyword', validationField);
      messages.name = translatedMessage;
      if (shouldDisplayToast) {
        this.showMessage('error', translatedMessage);
      }
    }

    return { errorMessage: messages, title, titleToCheck };
  }

  // Find Duplicates of case insensitive value in array of objects
  findDuplicates(arr: any, keyName: any = 'name'): any {
    const counts = arr.reduce(
      (accumulator, currentObj) => {
        accumulator[currentObj[keyName]] = (accumulator[currentObj[keyName]?.toLowerCase()] || 0) + 1;
        return accumulator;
      },
      {} as Record<string, number>
    );

    return Object.keys(counts).filter((keyVal) => counts[keyVal] > 1);
  }

  isValidEmail(val) {
    const reg =
      /^(?![.!#$%&'*+/=?^_`{|}~-])(?!.*[!#$%&'*+/=?^_`{|}~.-]{2,})[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!reg.exec(val)) {
      return false;
    }
    return true;
  }

  isValidAlphaNumericUnderscoreValue(value: string): boolean {
    const myRe = /^[a-zA-Z][a-zA-Z0-9_]*$/g;
    const array = myRe.exec(value);
    if (!array) {
      this.showMessage('error', this.translate.instant('ALPHANUMEREIC_UNDERSCORE_CHARACTERS_ALLOWED'));
      return false;
    }
    return true;
  }

  getSearchableReferredTableFields(tableInfo, tableId) {
    let searchableFields: any =
      Object.keys((tableInfo?.currentTableSearchableField as { fields: any })?.fields || {}) || [];
    let referredTableFields: any[] = [];
    let groupedFields: any[] = [];
    const currentTableForeignKeys = tableInfo.currentTableForeignKeys;
    if (currentTableForeignKeys) {
      Object.values(currentTableForeignKeys).forEach((foreignKey: any) => {
        // const foreignKey = currentTableForeignKeys[foreignKeySid];
        const foreignKeySid = foreignKey.sid;
        const refTableSearchableFields = tableInfo.currentTableSearchableField?.foreignKeyFields[foreignKeySid] || {};
        const foreignkeyGroup: { label: string; items: any[] } = {
          label: tableInfo?.currentTableForeignKeys?.find((fk) => fk.sid === foreignKeySid)?.fkName || '',
          items: []
        };
        if (refTableSearchableFields) {
          Object.values(refTableSearchableFields).forEach((field: any) => {
            let fieldDetails = tableInfo?.foreignTableFields?.[foreignKey?.parentTableSid]?.find(
              (f) =>
                f.sid === field.fieldSid &&
                f.f_type !== 'Image Carousel' &&
                f.f_type !== 'File Attachments' &&
                !f.f_transientField
            );
            if (fieldDetails) {
              fieldDetails = {
                ...fieldDetails,
                ...{
                  sid: `${foreignKeySid}_$_${fieldDetails.sid}`,
                  name: refTableSearchableFields.find((f) => f.sid === field.sid)?.foreignKeyAliasName,
                  f_tableId: tableId,
                  fkTableSid: foreignKey?.parentTableSid,
                  fkId: foreignKeySid,
                  fkAppId: fieldDetails?.aid
                }
              };
              referredTableFields = [...referredTableFields, fieldDetails];
              foreignkeyGroup.items = [...foreignkeyGroup.items, fieldDetails];
              // include the foreign key searchable fields also in the searchableFields array
              searchableFields = [...searchableFields, fieldDetails.sid];
            }
          });
        }
        groupedFields = [...groupedFields, foreignkeyGroup];
      });
    }
    return {
      searchableFields: searchableFields,
      referredTableFields: referredTableFields,
      groupedFields: groupedFields
    };
  }

  formatModifiedDateTimestamp(modifiedDate: { seconds: number; nanoseconds: number }): string {
    if (!modifiedDate || typeof modifiedDate.seconds !== 'number' || typeof modifiedDate.nanoseconds !== 'number') {
      return '';
    }
    const timestampMs = modifiedDate.seconds * 1000 + Math.floor(modifiedDate.nanoseconds / 1e6);
    const date = new Date(timestampMs);
    return this.datePipe.transform(date, 'MMM d, hh:mm a') || '';
  }
  subscriptions: Subscription[] = [];
  handleDeletedIds(
    deletedIds: any[],
    messagetype: boolean,
    message: string,
    showConfirmation: boolean = true,
    type: string,
    callback?: (msg: string) => void
  ) {
    if (messagetype) {
      callback?.(message);
      this.confirmationDialogService.confirm({
        message,
        acceptLabel: String(this.translate.instant('OK')),
        accept: () => {
          /* empty */
        },
        rejectVisible: false
      });
    } else {
      deletedIds.forEach((id, i) => {
        const idsdeleted = this.bs.get(ApiConstants.getDeletedRecord, { sid: id }).subscribe({
          next: (res) => {
            let translationKey = '';
            switch (type) {
              case 'lookup':
                translationKey = 'OUTPUT_MAPPING_DEL';
                break;

              case 'querybuilder':
                translationKey = 'QBUILDER_FLD_DEL';
                break;

              case 'parammapping':
                translationKey = 'QUERY_PARAM_MAP_DEL';
                break;

              case 'childfieldDelete':
                translationKey = 'CHILD_FIELD_DELETED_FK';
                break;

              case 'formulaBuilderFieldTypeChange':
                translationKey = 'FORMULA_BUILDER_FIELD_TYPE_CHANGED';
                break;

              case 'formulaBuilderFieldDeleted':
                translationKey = 'FORMULA_BUILDER_FIELD_DELETED';
                break;

              case 'runtimeFieldDeleted':
                translationKey = 'RT_FIELD_REMOVED';
                break;

              case 'runtimeFieldTypeMismatch':
                translationKey = 'RT_FIELD_TYPE_MISMATCH';
                break;

              case 'runtimeStatusFieldDeleted':
                translationKey = 'RT_STATUS_FIELD_REMOVED';
                break;

              case 'runtimeStatusFieldTypeMismatch':
                translationKey = 'RT_STATUS_FIELD_TYPE_CHANGED';
                break;

              case 'pagemapping':
                translationKey = 'QV_MAP_NO_LONGER_FLD';
                break;
              default:
                translationKey = '';
                break;
            }

            if (type) {
              message += this.translate.instant(translationKey).replace('{field}', res?.name);
            } else {
              message += (message ? ', ' : '') + res?.name;
            }
            if (i === deletedIds.length - 1) {
              callback?.(message);
              if (showConfirmation) {
                this.confirmationDialogService.confirm({
                  message,
                  acceptLabel: String(this.translate.instant('OK')),
                  accept: () => {
                    /* empty */
                  },
                  rejectVisible: false
                });
              }
            }
          },
          error: (err: any) => {
            callback?.('error message');
          }
        });
        this.subscriptions.push(idsdeleted);
      });
    }
  }

  isFieldNoLonger(data: any, array: any[], component: string, key?: any): boolean {
    if (!key) {
      if (component === 'query-param-map') {
        return !array.find((f: any) => f.sid === data.listField?.sid);
      } else if (component === 'query-mapping') {
        return !array.find((f: any) => f.sid === data.tableField?.sid);
      } else if (component === 'pg-lookup-output-mapping' || component === 'pg-lookup-input-mapping') {
        return !array.find((f: any) => f.sid === data.tableField?.sid);
      } else if (component === 'pg-lookup-display-field') {
        return !array.find((f: any) => f.sid === data.displayField?.sid);
      } else if (component === 'qb-builder') {
        return !array.find(
          (f: any) =>
            f.sid ===
            (data.lhs?.fieldId.includes('currentUser-')
              ? data.lhs?.fieldId.replace('currentUser-', '')
              : data.lhs?.fieldId)
        );
      }
    } else {
      if (component === 'query-mapping') {
        return !array.find((f: any) => f.sid === data[key].sid);
      }
    }
    return false;
  }

  isFieldMismatch(
    data: any,
    sourceArray: any[],
    targetArray: any[],
    component: string,
    sourceKey?: any,
    targetKey?: any,
    isLhsTarget?: boolean
  ): boolean {
    try {
      let sourceField = null;
      let targetField = null;
      if (component === 'query-param-map') {
        if (data?.listField && data?.detailField) {
          sourceField = sourceArray.find((f: any) => f.sid === data.listField?.sid);
          targetField = targetArray.find((f: any) => f.sid === data.detailField?.sid);
        }
      } else if (component === 'query-mapping' && !sourceKey && !targetKey) {
        if (data?.lookupField && data?.tableField) {
          sourceField = sourceArray.find((f: any) => f.sid === data.lookupField.sid);
          targetField = targetArray.find((p: any) => p.sid === data.tableField?.sid);
        }
      } else if (component === 'query-mapping' && sourceKey && targetKey) {
        if (data?.[sourceKey]?.sid && data?.[targetKey]?.sid) {
          sourceField = sourceArray.find((f: any) => f.sid === data[sourceKey].sid);
          targetField = targetArray.find((f: any) => f.sid === data[targetKey].sid);
        }
      } else if (component === 'pg-lookup-output-mapping' || component === 'pg-lookup-input-mapping') {
        if (data?.lookupField && data?.tableField) {
          sourceField = sourceArray.find((f: any) => f.sid === data.lookupField?.sid);
          targetField = targetArray.find((p: any) => p.sid === data.tableField?.sid);
        }
      } else if (component === 'qb-builder') {
        if (data?.rhs && data?.lhs) {
          let lefttype = '';
          let righttype = '';
          if(data.lhs?.type){
            lefttype = data.lhs.type;
          }
          targetField = targetArray.find((p: any) => p.value === data.rhs?.value);
          if(targetField && (targetField as any)?.type){
            righttype = this.normalizePageVariableType((targetField as any).type);
          }
          if (!lefttype || !righttype) {
            return false;
          }
          if (!GlobalConstants.paramMappDataTyp[lefttype]) {
            return false;
          }
          return !GlobalConstants.paramMappDataTyp[lefttype]?.includes(righttype);
        }
      }
      if (!sourceField || !targetField) {
        return false;
      }
      // Find direction based on component of mapping: input mappings and query mappings without keys have LHS as target
      const LhsTarget = component === 'pg-lookup-input-mapping'
        || component === 'query-param-map'
        || (component === 'query-mapping' && !sourceKey && !targetKey);
      const lhsIsTarget = isLhsTarget ?? LhsTarget;
      return this.typeMismatchWarningMessage(sourceField, targetField, lhsIsTarget);
    } catch (e) {
      return false;
    }
  }

  typeMismatchWarningMessage(sourceField: { f_type?: string }, targetField: { f_type?: string }, isLhsTarget?: boolean): boolean {
    const sourceType = sourceField.f_type?.toLowerCase();
    const targetType = targetField.f_type?.toLowerCase() || targetField['type']?.toLowerCase();
    if (!sourceType || !targetType) {
      return false;
    }
    // If LHS is target, we need reverse lookup: check if targetType (RHS/source) can map to sourceType (LHS/target)
    if (isLhsTarget) {
      return !this.getSourceTypesForTarget(sourceType, GlobalConstants.paramMappDataTyp)?.includes(targetType);
    }
    return !GlobalConstants.paramMappDataTyp[sourceType]?.includes(targetType);
  }

  formatDateForConsole(d: Date): string {
    return this.datePipe.transform(d, 'yyyyMMdd hh:mm:ss a') || '';
  }

  getLocalStorageKeyForJexcelSheetRoles(componentId: string) {
    return 'jexcelSheetRolesFilter_' + this.appGlobalService.get('appId') + '_' + componentId;
  }

  normalizePageVariableType(type: string): string {
    if (!type) return '';
    const lower = type.toLowerCase();
    if (lower === 'date_only') return 'date';
    if (lower === 'date_time') return 'datetime';
    return lower;
  }


  determineRightType(rhs: any, fieldType: string): string {
    if (!rhs || fieldType !== 'value' || rhs.value === null || rhs.value === undefined) {
      return '';
    }
    if (rhs.value instanceof Date) {
      return this.determineDateType(rhs.value);
    }
    const valueType = typeof rhs.value;
    if (valueType === 'boolean') {
      return 'boolean';
    }
    if (valueType === 'number') {
      return Number.isInteger(rhs.value as number) ? 'number' : 'double';
    }
    if (valueType === 'string') {
      return this.determineStringType(rhs.value as string);
    }
    return 'string';
  }

  private determineDateType(d: Date): string {
    const isDateOnly = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
    return isDateOnly ? 'date' : 'datetime';
  }

  private determineStringType(stringValue: string): string {
    if (stringValue === 'today' || stringValue === 'tomorrow' || stringValue === 'yesterday') {
      return 'customTime';
    }
    if (stringValue.toLowerCase() === 'true' || stringValue.toLowerCase() === 'false') {
      return 'boolean';
    }
    if (this.isValidEmail(stringValue)) {
      return 'email';
    }
    if (GlobalConstants.yesNoRegex.test(stringValue.trim())) {
      return 'yes no';
    }
    if (!isNaN(parseFloat(stringValue)) && isFinite(Number(stringValue))) {
      return 'string';
    }
    if (!isNaN(Date.parse(stringValue))) {
      return GlobalConstants.dateTimeRegex.test(stringValue) ? 'datetime' : 'date';
    }
    return 'string';
  }

  private readonly reservedBranchNames = ['main', 'master', 'head', 'develop', 'release', 'origin', 'upstream', 'abstract','id', 'alter'];

  private readonly rules = [
    { message: 'name cannot exceed 50 characters', validate: (n: string) => n.length > 50 },
    { message: 'name cannot be "HEAD"', validate: (n: string) => n.toUpperCase() === 'HEAD' },
    { message: 'name cannot be a reserved name', validate: (n: string) => this.reservedBranchNames.includes(n.toLowerCase()) },
     {message: 'name cannot contain space', validate: (n: string) => n.includes(' ')},
    {
      message: 'name contains invalid characters',
      validate: (n: string) => !/^[A-Za-z0-9._/-]+$/.test(n)
    },
    { message: 'name cannot contain consecutive dots (..)', validate: (n: string) => n.includes('..') },
    { message: 'name cannot end with .lock', validate: (n: string) => n.endsWith('.lock') },
    { message: 'name cannot contain "@{"', validate: (n: string) => n.includes('@{') },
    {
      message: 'name cannot start or end with slash (/)',
      validate: (n: string) => n.startsWith('/') || n.endsWith('/')
    },
    { message: 'name cannot start with dash (-)', validate: (n: string) => n.startsWith('-') },
    { message: 'name cannot start or end with dot (.)', validate: (n: string) => n.startsWith('.') || n.endsWith('.') },
    { message: 'name cannot contain consecutive slashes (//)', validate: (n: string) => n.includes('//') },
    { message: 'name cannot contain slash followed by dot (/.) ', validate: (n: string) => /\/\./.test(n) },
    { message: 'name cannot contain /../', validate: (n: string) => n.includes('/../') }
  ];

  gitNameValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value?.trim();
      if (!value) {
        return { gitNameError: 'name cannot be empty' };
      }

      for (const rule of this.rules) {
        if (rule.validate(value)) {
          // When the name has invalid characters, shows the invalid characters also in the error message
          if(rule.message === 'name contains invalid characters'){
            const invalidChars = new Set(value.match(/[^A-Za-z0-9._/-]/g));
            return { gitNameError: rule.message + ' ' + Array.from(invalidChars).join(', ') };
          }
          return { gitNameError: rule.message };
        }
      }

      // Check against reserved keywords
      const reservedKeywords = this.getRestrictedKeywords();
      if (reservedKeywords.includes(value.toLowerCase())) {
        return { gitNameError: String(this.translate.instant('NAME_IS_RESERVED_KEYWORD')) };
      }

      // Check against restricted SQL keywords
      const restrictedSqlKeywords = this.getRestrictedSQLKeywords();
      if (restrictedSqlKeywords.includes(value.toLowerCase())) {
        return { gitNameError: String(this.translate.instant('NAME_IS_RESERVED_KEYWORD')) };
      }

      return null;
    };
  }

  private readonly showBranchPopup = new Subject<void>();
  showBranchPopup$ = this.showBranchPopup.asObservable();

  openBranchPopup() {
    this.showBranchPopup.next();
  }

  private readonly showTagPopup = new Subject<void>();
  showTagPopup$ = this.showTagPopup.asObservable();

  openTagPopup() {
    this.showTagPopup.next();
  }
  // Shows a confirmation dialog when there are no references for an item
  // For Application/Database types, shows suite-specific message
  showNoReferencesDialog(compName: string, compType: string = 'item') {
    const formattedType = compType.charAt(0).toUpperCase() + compType.slice(1).toLowerCase();
    const messageKey = this.suiteTypes.includes(formattedType) ? 'NO_REFERENCES_SUITE_MSG' : 'NO_REFERENCES_MSG';
    this.confirmationDialogService.confirm({
      key: 'noReferencesDialog',
      message: this.translate.instant(messageKey, { compName: compName, compType: formattedType }),
      accept: () => {
        // Do Nothing
      },
      rejectVisible: false,
      acceptLabel: this.translate.instant('CLOSE'),
      acceptButtonStyleClass: 'btn-transparent-grey'
    });
  }

  /**
   * Applies date range filters to a filters object.
   * Converts date range array to ISO string format for API requests.
   * If only start date is provided, sets end date to end of that day (23:59:59.999).
   *
   * @param filters - The filters object to add date range to
   * @param dateRange - Array of dates [startDate, endDate?]
   * @returns The filters object with dateFrom and dateTo properties added
   */
  applyDateRangeFilter<T extends { dateFrom?: string; dateTo?: string }>(
    filters: T,
    dateRange: Date[] | null | undefined
  ): T {
    if (dateRange && dateRange.length > 0 && dateRange[0]) {
      filters.dateFrom = dateRange[0].toISOString();
      if (dateRange.length > 1 && dateRange[1]) {
        filters.dateTo = dateRange[1].toISOString();
      } else {
        const endOfDay = new Date(dateRange[0]);
        endOfDay.setHours(23, 59, 59, 999);
        filters.dateTo = endOfDay.toISOString();
      }
    }
    return filters;
  }

  extractQueryStrings(query) {
    if (!query || typeof query !== 'string') {
      return [];
    }

    const regex = GlobalConstants.queryStringRegex.qStringMatchPattern;
    const matches = [...query.matchAll(regex)].map((match) => match[1]);

    return matches;
  }

  changesMadeInQueryString(oldQuery, newQuery) {
    const oldQueryQS = this.extractQueryStrings(oldQuery);
    const newQueryQS = this.extractQueryStrings(newQuery);
    const queryStringMap = this.appGlobalService.get('queryStrings') || {};
    const addedSet = newQueryQS.filter((item) => !oldQueryQS.find((oldItem) => oldItem === item));
    const removedSet = oldQueryQS.filter((item) => !newQueryQS.find((newItem) => newItem === item));

    // Convert arrays to maps with sid -> name mapping
    const addedMap = {};
    addedSet.forEach((sid) => {
      addedMap[sid] = queryStringMap[sid] || sid;
    });

    const removedMap = {};
    removedSet.forEach((sid) => {
      removedMap[sid] = queryStringMap[sid] || sid;
    });

    let changes = {};
    if (Object.keys(addedMap).length > 0) {
      changes['ADDED'] = addedMap;
    }
    if (Object.keys(removedMap).length > 0) {
      changes['REMOVED'] = removedMap;
    }
    return changes;
  }

  flushDebounce(debouncer: any): void {
    debouncer?.flush?.();
  }


  /**
   * Retrieves a cookie value by name
   * @param name - The name of the cookie to retrieve
   * @returns The cookie value or null if not found
   */
  getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  getSimilarity(s1: string, s2: string): number {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    const longerLength = longer.length;
    if (longerLength === 0) {
      return 1.0;
    }
    return (longerLength - this.editDistance(longer, shorter)) / parseFloat(longerLength.toString());
  }

  editDistance(s1: string, s2: string): number {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();
    const costs: number[] = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i == 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) != s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) {
        costs[s2.length] = lastValue;
      }
    }
    return costs[s2.length];
  }

  /**
   * Converts a Firebase Timestamp object {seconds, nanoseconds} to a JavaScript Date.
   * @param timestamp The Firebase Timestamp object
   * @returns A JavaScript Date or the original value if invalid
   */
  getJsDateFromTimestamp(timestamp: any): any {
    if (timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
    return timestamp;
  }
  /**
   * Recursively searches a nested structure to find a node matching the specified key-value pair.
   * Traverses through 'children' arrays at each level.
   * @param data - The nested structure to search (can be a single node object or an array of nodes, each with optional 'children' arrays)
   * @param nodeKey - The property key to match against
   * @param nodeValue - The value to match for the specified key
   * @returns The matching node or null if not found
   */
  findNodeByKeyValue(data: any, nodeKey: string, nodeValue: any): any {
    if (!data) {
      return null;
    }

    if (Array.isArray(data)) {
      return this.findNodeInArray(data, nodeKey, nodeValue);
    }

    if (typeof data === 'object') {
      return this.findNodeInObject(data, nodeKey, nodeValue);
    }

    return null;
  }

  private findNodeInArray(dataArray: any[], nodeKey: string, nodeValue: any): any {
    for (const item of dataArray) {
      const result = this.findNodeByKeyValue(item, nodeKey, nodeValue);
      if (result) {
        return result;
      }
    }
    return null;
  }

  private findNodeInObject(data: any, nodeKey: string, nodeValue: any): any {
    if (data?.[nodeKey] === nodeValue) {
      return data;
    }

    if (data.children && Array.isArray(data.children)) {
      return this.findNodeByKeyValue(data.children, nodeKey, nodeValue);
    }

    return null;
  }

  /** 
   * Format an UPPER_SNAKE_CASE string to Title Case
   * e.g. 'MERGE_SOURCE' -> 'Pick Source'
   */
  formatAction(action: string): string {
    if (!action) return '';
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Gets all source types that can map TO the given target type.
   * Use this when filtering source fields based on a selected target field.
   * @param targetType - The target field type
   * @param mappingConfig - The mapping configuration object (GlobalConstants.paramMappDataTyp)
   * @returns Array of source types that can map to the target
   */
  getSourceTypesForTarget(targetType: string, mappingConfig: Record<string, string[]>): string[] {
    const sources: string[] = [];
    for (const [source, targets] of Object.entries(mappingConfig)) {
      if (targets.includes(targetType)) {
        sources.push(source);
      }
    }
    return sources;
  }

  /**

   * Syncs form control states based on disallowEdit flag.
   * When disallowEdit is true, all controls are disabled and their original state is preserved in lockState.
   * When disallowEdit is false, controls are restored to their original state.
   * 
   * @param formGroup - The FormGroup to sync
   * @param lockState - A Map that stores the original disabled state of each control
   * @param disallowEdit - Whether editing should be disallowed
   */
  syncFormLockState(formGroup: any, lockState: Map<string, boolean>, disallowEdit: boolean): void {
    if (!formGroup?.controls) {
      return;
    }

    Object.keys(formGroup.controls).forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (!control) {
        return;
      }

      if (disallowEdit) {
        if (!lockState.has(controlName)) {
          lockState.set(controlName, control.disabled);
        }
        control.disable({ emitEvent: false });
        return;
      }

      if (lockState.has(controlName)) {
        const wasDisabled = lockState.get(controlName);
        if (wasDisabled) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
    });

    if (!disallowEdit) {
      lockState.clear();
    }
  }

  /**
   * Handles paste events on form controls by trimming pasted content
   * and marking the control as dirty to ensure form state is updated.
   *
   * @param formGroup - The FormGroup containing the control
   * @param controlName - The name of the form control being pasted into
   */
  handlePaste(formGroup: any, controlName: string): void {
    const control = formGroup?.get(controlName);
    setTimeout(() => {
      const trimmedValue = control?.value?.trim() || '';
      control?.setValue(trimmedValue);
      control?.markAsDirty();
      control?.updateValueAndValidity();
    });
  }

  /**
   * Checks if a search form is empty (all fields are falsy).
   *
   * @param formGroup - The advanced search FormGroup to check
   * @param fields - Array of field names to check
   * @returns true if the form is empty or doesn't exist
   */
  isSearchFormEmpty(formGroup: any, fields: string[]): boolean {
    if (!formGroup) {
      return true;
    }
    const formValues = formGroup.value;
    if (!formValues) {
      return true;
    }
    return fields.every((field) => !formValues[field]);
  }
}

